import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { useAuth } from "../../contexts/AuthContext";
import { getDayPost, generateDateID } from "../../services/dayPostService";
import { userService } from "../../services/userService";
import { DayPost, TrainingLog, ActivityLog } from "../../types/post";
import { useSubscription } from "../../providers/SubscriptionProvider";

import {
  useDayPostList,
  useDayPostSubmission,
  usePendingDayPostOperations,
} from "../../hooks/useDayPostData";
import { useFirebaseData } from "../../hooks/useFirebaseData";
import { UserPublicData } from "../../types/user";
import {
  ChevronLeftIcon,
  XMarkIcon,
  BellIcon,
  EyeIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import TrainingSessionCard from "../../components/dashboard/cards/TrainingSessionCard";
import Header from "../../components/layout/Header";
import TrainingLogForm from "../../components/DayPost/training-log/TrainingLogForm";
import DeleteTrainingLogDialog from "../../components/DayPost/training-log/DeleteTrainingLogDialog";
import { getLogTrainingTypeNames } from "../../components/DayPost/training-log/trainingLogUtils";
import { trainingTypeService } from "../../services/trainingTypeService";
import ComingSoon from "../../components/ComingSoon";
import { useThemeStore } from "../../stores/themeStore";
import { displayWorkloadFactor } from "../../utils/loadBalanceDisplay";
interface TrainingData {
  date: string;
  dateLabel: string;
  totalLoad: number;
  sessionCount: number;
  averageRPE: number;
  totalDuration: number;
  totalWork: number; // Work in kilojoules from linked activities
  hasData: boolean;
  workloadFactor?: number; // Workload factor from peak score log
  [key: string]: string | number | boolean | undefined; // Include boolean for hasData and undefined for optional fields
}

const TrainingInsights: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { resolvedTheme } = useThemeStore();
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const isDark = resolvedTheme === "peakx-dark";
  const nivoTheme = {
    axis: {
      ticks: {
        text: {
          fill: isDark ? "#e5e5e5" : "#374151",
        },
      },
    },
    grid: {
      line: {
        stroke: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
        strokeWidth: 1,
      },
    },
  };

  // Get URL parameter to check if viewing another user's data
  const queryUid = useMemo(
    () => new URLSearchParams(location.search).get("uid"),
    [location.search],
  );

  // If viewing another user's insights, fetch their public profile
  const { data: viewedUser } = useFirebaseData<UserPublicData>({
    collection: "users",
    docId: queryUid || "",
    enabled: !!queryUid,
    maxAge: 600,
    enableRealtimeUpdates: true,
    fallbackToCache: true,
  });

  // Use cached day posts for Day/Week/Month views
  // Fetch 28 days to support monthly session type breakdown
  const {
    dayPosts,
    loading: cachedLoading,
    error: cachedError,
    isFromCache,
    isOffline,
    fetchDayPostsData,
  } = useDayPostList({
    userId: queryUid || undefined,
    disableCache: !!queryUid,
    disableAutoLoad: true, // We'll manually load 28 days
  });

  // Fetch 56 days of data on component mount (28 days + previous 28 days for comparison)
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const {
    subscription: userSubscription,
    hasSubscription,
    isPremium,
    isPlus,
    refreshSubscription,
    loading: subscriptionProviderLoading,
  } = useSubscription();
  useEffect(() => {
    const effectiveUserId = queryUid || currentUser?.uid;
    if (effectiveUserId && !hasInitialLoad) {
      fetchDayPostsData(false, 56); // Fetch 56 days of data for monthly view with comparison
      setHasInitialLoad(true);
    }
  }, [queryUid, currentUser?.uid, hasInitialLoad, fetchDayPostsData]);

  // Use submission hook for editing logs
  const { deleteTrainingLogById } = useDayPostSubmission(
    async () => {
      // Fetch fresh data after successful submission
      await fetchDayPostsData(false);
    },
  );
  const { addPendingItem } = usePendingDayPostOperations(async () => {
    await fetchDayPostsData(false);
  });

  // State for Month/Quarter data (traditional Firebase fetching)
  const [monthlyData, setMonthlyData] = useState<TrainingData[]>([]);
  const [quarterlyData, setQuarterlyData] = useState<TrainingData[]>([]);
  const [monthQuarterLoading, setMonthQuarterLoading] = useState(false);
  const [monthQuarterError, setMonthQuarterError] = useState<string | null>(
    null,
  );

  const [weeklyLoadGoal, setWeeklyLoadGoal] = useState<number>(2800); // Default goal
  const [selectedTab, setSelectedTab] = useState<
    "Day" | "Week" | "Month" | "Quarter" | "Custom"
  >("Day");

  // State for toggling average lines in Week view
  const [show7DayAverage, setShow7DayAverage] = useState<boolean>(false);
  const [show28DayAverage, setShow28DayAverage] = useState<boolean>(false);
  const [showPrevious7DayAverage, setShowPrevious7DayAverage] =
    useState<boolean>(false);
  const [showPrevious28DayAverage, setShowPrevious28DayAverage] =
    useState<boolean>(false);
  const [show12WeekAverage, setShow12WeekAverage] = useState<boolean>(false);

  // State for Month view display mode (daily vs weekly)
  const [monthViewMode, setMonthViewMode] = useState<"daily" | "weekly">(
    "daily",
  );
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  // Hover and pin state for chart interaction
  const [hoveredSlice, setHoveredSlice] = useState<any>(null);
  const [pinnedSlice, setPinnedSlice] = useState<any>(null);

  // Use subscription status directly from the hook
  // Only Premium/Platinum/Coach users get access to advanced graph features
  const hasQuarterlyAccess = isPremium;
  const hasCustomDateAccess = isPremium;
  const hasMonthlyAccess = isPremium || isPlus; // Plus includes 28 Days Analytics
  const hasWeeklyAccess = isPremium || isPlus; // Plus includes 7 & 28 Days Analytics

  // State for custom date range
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");

  // State for Load Breakdown expansion
  const [showAllWeekBreakdown, setShowAllWeekBreakdown] = useState(false);
  const [showAllMonthBreakdown, setShowAllMonthBreakdown] = useState(false);
  const [showAllQuarterBreakdown, setShowAllQuarterBreakdown] = useState(false);
  const [showAllCustomBreakdown, setShowAllCustomBreakdown] = useState(false);
  const [customRangeData, setCustomRangeData] = useState<TrainingData[]>([]);
  const [customRangeLoading, setCustomRangeLoading] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  // State for editing training logs
  const [editingTrainingLog, setEditingTrainingLog] = useState<{
    id: string;
    data: TrainingLog;
    dateID: string;
  } | null>(null);
  const [showTrainingForm, setShowTrainingForm] = useState(false);

  // Get date from URL parameter, default to today
  const dateParam = searchParams.get("date");
  const targetDate = useMemo(() => {
    if (dateParam && /^\d{8}$/.test(dateParam)) {
      const year = parseInt(dateParam.substring(0, 4));
      const month = parseInt(dateParam.substring(4, 6)) - 1; // Month is 0-indexed
      const day = parseInt(dateParam.substring(6, 8));
      return new Date(year, month, day);
    }
    return new Date();
  }, [dateParam]);

  // Helper functions (moved before usage)
  // Generate last 7 days ending with the target date (rolling 7-day period)
  const generateWeekDates = (targetDate: Date): Date[] => {
    const dates: Date[] = [];
    const current = new Date(targetDate);

    // Generate 7 days ending with targetDate (going back 6 days from target)
    for (let i = 6; i >= 0; i--) {
      const date = new Date(current);
      date.setDate(current.getDate() - i);
      dates.push(date);
    }

    return dates;
  };

  // Helper function for getting linked activity
  const getLinkedActivityForTraining = (
    trainingLog: TrainingLog & { id: string },
    activities: (ActivityLog & { id: string })[],
  ): (ActivityLog & { id: string }) | null => {
    if (!trainingLog.linkedActivityLogID) return null;
    return (
      activities.find(
        (activity) => activity.id === trainingLog.linkedActivityLogID,
      ) || null
    );
  };

  // Calculate training metrics from logs and linked activities
  const calculateTrainingMetrics = (
    trainingLogs: (TrainingLog & { id: string })[],
    activityLogs: (ActivityLog & { id: string })[] = [],
  ): {
    totalLoad: number;
    sessionCount: number;
    averageRPE: number;
    totalDuration: number;
    totalWork: number;
  } => {
    if (trainingLogs.length === 0) {
      return {
        totalLoad: 0,
        sessionCount: 0,
        averageRPE: 0,
        totalDuration: 0,
        totalWork: 0,
      };
    }

    const totalLoad = trainingLogs.reduce(
      (sum, log) => sum + (log.load || 0),
      0,
    );
    const sessionCount = trainingLogs.length;
    const validRPEs = trainingLogs.filter((log) => log.rpe > 0);
    const averageRPE =
      validRPEs.length > 0
        ? validRPEs.reduce((sum, log) => sum + log.rpe, 0) / validRPEs.length
        : 0;
    const totalDuration = trainingLogs.reduce(
      (sum, log) => sum + (log.duration || 0),
      0,
    );

    // Calculate total work from linked activities
    const totalWork = trainingLogs.reduce((sum, log) => {
      if (log.linkedActivityLogID) {
        const linkedActivity = activityLogs.find(
          (activity) => activity.id === log.linkedActivityLogID,
        );
        return sum + (linkedActivity?.workKilojoules || 0);
      }
      return sum;
    }, 0);

    return { totalLoad, sessionCount, averageRPE, totalDuration, totalWork };
  };

  // Format date for display
  const formatDateLabel = (date: Date): string => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  };

  // Extract current day's data from cached dayPosts (same as Dashboard)
  const currentDayPost = useMemo(() => {
    const currentDateID = generateDateID(targetDate);
    return dayPosts.find((dayPost) => dayPost.dateID === currentDateID) || null;
  }, [dayPosts, targetDate]);

  // Undefined while cold-start load balance is provisional, so the day block
  // can narrow on a number instead of the optional day post.
  const dayLoadBalance = displayWorkloadFactor(currentDayPost?.peakScoreLog);

  // Current day training sessions
  const currentDayTraining = useMemo(() => {
    return currentDayPost?.trainingLogs || [];
  }, [currentDayPost]);

  // Generate week data from cached dayPosts
  const trainingData = useMemo(() => {
    const weekDates = generateWeekDates(targetDate);
    const data = weekDates.map((date) => {
      const dateID = generateDateID(date);
      const dayPost = dayPosts.find((post) => post.dateID === dateID);
      const metrics = dayPost
        ? calculateTrainingMetrics(dayPost.trainingLogs, dayPost.activityLogs)
        : {
            totalLoad: 0,
            sessionCount: 0,
            averageRPE: 0,
            totalDuration: 0,
            totalWork: 0,
          };

      return {
        date: dateID,
        dateLabel: formatDateLabel(date),
        ...metrics,
        hasData: dayPost !== null && (dayPost?.trainingLogs?.length || 0) > 0,
        workloadFactor: displayWorkloadFactor(dayPost?.peakScoreLog),
      };
    });
    return data;
  }, [dayPosts, targetDate]);

  // Navigate to specific training log insights
  const goToTrainingLogInsights = (trainingLogId: string) => {
    navigate(`/insights/trainingLog/${trainingLogId}`);
  };

  // Handle training log edit
  const handleEditTrainingLog = useCallback(
    (logId: string, log: TrainingLog & { id: string }) => {
      // Prevent editing when viewing another user's insights
      if (queryUid) return;

      const dateID = generateDateID(targetDate);
      setEditingTrainingLog({ id: logId, data: log, dateID });
      setShowTrainingForm(true);
    },
    [queryUid, targetDate],
  );

  // Training log pending deletion; the dialog asks about linked sessions and
  // activity data (plan decision 5) before anything is removed.
  const [trainingLogToDelete, setTrainingLogToDelete] = useState<
    (TrainingLog & { id: string }) | null
  >(null);
  const [isDeletingTrainingLog, setIsDeletingTrainingLog] = useState(false);

  // Handle training log delete (opens the confirmation)
  const handleDeleteTrainingLog = useCallback(
    (logId: string) => {
      // Prevent deleting when viewing another user's insights
      if (queryUid || !currentUser?.uid) return;
      const log = currentDayTraining.find((l) => l.id === logId);
      if (log) setTrainingLogToDelete(log);
    },
    [queryUid, currentUser?.uid, currentDayTraining],
  );

  const handleConfirmDeleteTrainingLog = useCallback(
    async (deleteLinkedSessionsAndActivity: boolean) => {
      if (!trainingLogToDelete || !currentUser?.uid) return;
      try {
        setIsDeletingTrainingLog(true);
        const dateID =
          trainingLogToDelete.dateID || generateDateID(targetDate);
        await deleteTrainingLogById(
          trainingLogToDelete.id,
          dateID,
          deleteLinkedSessionsAndActivity,
          addPendingItem,
        );

        // Refresh the data
        await fetchDayPostsData(false);
      } catch (error) {
        console.error("Error deleting training log:", error);
      } finally {
        setIsDeletingTrainingLog(false);
        setTrainingLogToDelete(null);
      }
    },
    [
      trainingLogToDelete,
      currentUser?.uid,
      targetDate,
      deleteTrainingLogById,
      addPendingItem,
      fetchDayPostsData,
    ],
  );

  // Handle training form submit
  // Handle training form close
  const handleCloseTrainingForm = useCallback(() => {
    setShowTrainingForm(false);
    setEditingTrainingLog(null);
  }, []);

  // Generate past 30 days
  const generatePast30Days = (endDate: Date): Date[] => {
    const dates: Date[] = [];
    for (let i = 29; i >= 0; i--) {
      // Get 30 days of data
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      dates.push(date);
    }
    return dates;
  };

  // Generate 8 weeks (56 days) for monthly view using 7-day rolling windows
  // This includes the last 28 days + previous 28 days for comparison
  const generateMonthlyWeekDates = (targetDate: Date): Date[] => {
    const dates: Date[] = [];

    // Generate 8 weeks of 7-day rolling windows ending with targetDate
    // Week 8 (most recent) ends on targetDate, Week 7 ends 7 days before, etc.
    for (let weekOffset = 7; weekOffset >= 0; weekOffset--) {
      const weekEndDate = new Date(targetDate);
      weekEndDate.setDate(targetDate.getDate() - weekOffset * 7);

      // Generate 7 days ending with weekEndDate (same logic as generateWeekDates)
      for (let i = 6; i >= 0; i--) {
        const date = new Date(weekEndDate);
        date.setDate(weekEndDate.getDate() - i);
        dates.push(date);
      }
    }

    return dates;
  };

  // Format month date label for display
  const formatMonthDateLabel = (date: Date): string => {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  // Generate past 12 weeks of dates (weekly averages for quarterly view)
  // Uses same logic as Week tab for consistency - 7-day rolling windows
  const generatePast12Weeks = (endDate: Date): Date[][] => {
    const weeks: Date[][] = [];
    // Generate weeks in chronological order (oldest to newest)
    // Each week is a 7-day rolling window ending on a specific date
    for (let weekIndex = 11; weekIndex >= 0; weekIndex--) {
      const weekDates: Date[] = [];
      const weekEndDate = new Date(endDate);
      weekEndDate.setDate(endDate.getDate() - weekIndex * 7);

      // Generate 7 days ending with weekEndDate (same logic as generateWeekDates)
      for (let i = 6; i >= 0; i--) {
        const date = new Date(weekEndDate);
        date.setDate(weekEndDate.getDate() - i);
        weekDates.push(date);
      }
      weeks.push(weekDates);
    }
    return weeks;
  };

  // Fetch user's weekly load goal
  useEffect(() => {
    const fetchUserGoals = async () => {
      if (!currentUser?.uid) return;

      try {
        const { userData } = await userService.getUserProfile(currentUser.uid);
        if (userData) {
          const goal = (userData as any).weeklyLoadGoal;
          setWeeklyLoadGoal(typeof goal === "number" && goal > 0 ? goal : 2800);
        }
      } catch (error) {
        console.error("Error fetching user goals:", error);
        // Keep default goal if fetch fails
      }
    };

    fetchUserGoals();
  }, [currentUser?.uid]);

  // Fetch Month/Quarter data when page loads (proactive fetching for better UX)
  useEffect(() => {
    const fetchMonthQuarterData = async () => {
      const effectiveUserId = queryUid || currentUser?.uid;
      if (!effectiveUserId) {
        setMonthQuarterLoading(false);
        return;
      }

      // Check if we already have data for the current targetDate
      const hasMonthData = monthlyData.length === 56; // Should have 56 days for month view (28 days + previous 28 days)
      const hasQuarterData = quarterlyData.length === 12; // Should have 12 weeks for quarter view

      // Skip fetching if we already have both datasets
      if (hasMonthData && hasQuarterData) {
        // console.log("Skipping Month/Quarter fetch - data already available");
        return;
      }

      try {
        // console.log("Starting Month/Quarter data fetch on page load");
        setMonthQuarterLoading(true);
        setMonthQuarterError(null);

        // Fetch month data (4 weeks of daily data)
        const monthDates = generateMonthlyWeekDates(targetDate);
        const monthPromises = monthDates.map(async (date) => {
          const dateID = generateDateID(date);
          try {
            const dayPost = await getDayPost(effectiveUserId, dateID);
            const metrics = dayPost
              ? calculateTrainingMetrics(
                  dayPost.trainingLogs,
                  dayPost.activityLogs,
                )
              : {
                  totalLoad: 0,
                  sessionCount: 0,
                  averageRPE: 0,
                  totalDuration: 0,
                  totalWork: 0,
                };

            return {
              date: dateID,
              dateLabel: formatMonthDateLabel(date),
              ...metrics,
              hasData: dayPost !== null && dayPost.trainingLogs.length > 0,
              workloadFactor: displayWorkloadFactor(dayPost?.peakScoreLog),
            };
          } catch (error) {
            console.error(`Error fetching month data for ${dateID}:`, error);
            return {
              date: dateID,
              dateLabel: formatMonthDateLabel(date),
              totalLoad: 0,
              sessionCount: 0,
              averageRPE: 0,
              totalDuration: 0,
              totalWork: 0,
              hasData: false,
              workloadFactor: undefined,
            };
          }
        });

        const monthResults = await Promise.all(monthPromises);
        setMonthlyData(monthResults);

        // Fetch quarterly data (12 weeks of weekly averages)
        const quarterWeeks = generatePast12Weeks(targetDate);
        const quarterPromises = quarterWeeks.map(
          async (weekDates, weekIndex) => {
            // Get the first date of the 7-day window for labeling
            const weekStartDate = weekDates[0];
            const weekLabel = weekStartDate.toLocaleDateString("en-US", {
              day: "numeric",
              month: "short",
            });

            // Fetch all days in the week
            const weekPromises = weekDates.map(async (date) => {
              const dateID = generateDateID(date);
              try {
                const dayPost = await getDayPost(effectiveUserId, dateID);
                const metrics = calculateTrainingMetrics(
                  dayPost?.trainingLogs || [],
                  dayPost?.activityLogs || [],
                );
                return {
                  ...metrics,
                  hasData: dayPost !== null && dayPost.trainingLogs.length > 0,
                  workloadFactor: displayWorkloadFactor(dayPost?.peakScoreLog),
                };
              } catch (error) {
                return {
                  totalLoad: 0,
                  sessionCount: 0,
                  averageRPE: 0,
                  totalDuration: 0,
                  totalWork: 0,
                  hasData: false,
                  workloadFactor: undefined,
                };
              }
            });

            const weekResults = await Promise.all(weekPromises);

            // Calculate weekly averages
            const weekTotalLoad = weekResults.reduce(
              (sum, day) => sum + day.totalLoad,
              0,
            );
            const weekSessionCount = weekResults.reduce(
              (sum, day) => sum + day.sessionCount,
              0,
            );
            const weekTotalDuration = weekResults.reduce(
              (sum, day) => sum + day.totalDuration,
              0,
            );
            const weekTotalWork = weekResults.reduce(
              (sum, day) => sum + day.totalWork,
              0,
            );

            // Calculate average RPE for the week (only from days with training)
            const daysWithTraining = weekResults.filter((day) => day.hasData);
            const weekAverageRPE =
              daysWithTraining.length > 0
                ? daysWithTraining.reduce(
                    (sum, day) => sum + day.averageRPE,
                    0,
                  ) / daysWithTraining.length
                : 0;

            const hasWeekData = weekResults.some((day) => day.hasData);

            // Get workload factor from the last day of the week (most recent)
            const lastDayWorkloadFactor =
              weekResults[weekResults.length - 1]?.workloadFactor;

            return {
              date: generateDateID(weekStartDate),
              dateLabel: weekLabel,
              totalLoad: weekTotalLoad,
              sessionCount: weekSessionCount,
              averageRPE: weekAverageRPE,
              totalDuration: weekTotalDuration,
              totalWork: weekTotalWork,
              hasData: hasWeekData,
              workloadFactor: lastDayWorkloadFactor,
            };
          },
        );

        const quarterResults = await Promise.all(quarterPromises);
        // Keep chronological order (oldest to newest, left to right)
        setQuarterlyData(quarterResults);
      } catch (err) {
        console.error("Error fetching Month/Quarter training data:", err);
        setMonthQuarterError("Failed to load Month/Quarter training data");
      } finally {
        setMonthQuarterLoading(false);
      }
    };

    fetchMonthQuarterData();
  }, [
    currentUser?.uid,
    queryUid,
    targetDate,
    monthlyData.length,
    quarterlyData.length,
  ]);

  // Combined loading and error states
  const loading = useMemo(() => {
    if (selectedTab === "Day" || selectedTab === "Week") {
      return cachedLoading;
    } else if (selectedTab === "Month") {
      // Only show loading if we don't have month data yet
      const hasMonthData = monthlyData.length === 28;
      return monthQuarterLoading && !hasMonthData;
    } else if (selectedTab === "Quarter") {
      // Only show loading if we don't have quarter data yet
      const hasQuarterData = quarterlyData.length === 12;
      return monthQuarterLoading && !hasQuarterData;
    } else if (selectedTab === "Custom") {
      return customRangeLoading;
    }
    return false;
  }, [
    selectedTab,
    cachedLoading,
    monthQuarterLoading,
    monthlyData.length,
    quarterlyData.length,
    customRangeLoading,
  ]);

  const error = useMemo(() => {
    if (selectedTab === "Day" || selectedTab === "Week") {
      return cachedError?.message || null;
    } else if (selectedTab === "Custom") {
      return null; // Custom range handles its own errors
    } else {
      // Only show Month/Quarter errors if we don't have any data at all
      const hasMonthData = monthlyData.length > 0;
      const hasQuarterData = quarterlyData.length > 0;

      if (selectedTab === "Month" && !hasMonthData) {
        return monthQuarterError;
      } else if (selectedTab === "Quarter" && !hasQuarterData) {
        return monthQuarterError;
      }
      return null;
    }
  }, [
    selectedTab,
    cachedError,
    monthQuarterError,
    monthlyData.length,
    quarterlyData.length,
  ]);

  // Calculate average score based on selected tab
  const averageLoad = useMemo(() => {
    if (selectedTab === "Day") {
      const metrics = calculateTrainingMetrics(
        currentDayTraining,
        currentDayPost?.activityLogs || [],
      );
      return metrics.totalLoad;
    } else if (selectedTab === "Week") {
      // Include ALL days (treat no training as 0)
      if (trainingData.length === 0) return 0;
      return (
        trainingData.reduce((sum, d) => sum + d.totalLoad, 0) /
        trainingData.length
      );
    } else if (selectedTab === "Month") {
      // Include ALL days (treat no training as 0) - use last 28 days only
      if (monthlyData.length < 56) return 0;
      return (
        monthlyData.slice(28, 56).reduce((sum, d) => sum + d.totalLoad, 0) / 28
      );
    } else if (selectedTab === "Quarter") {
      // Calculate average daily load across all 12 weeks
      if (quarterlyData.length === 0) return 0;
      const totalDailyLoad = quarterlyData.reduce(
        (sum, d) => sum + d.totalLoad,
        0,
      );
      const totalDays = quarterlyData.length * 7; // 12 weeks × 7 days
      return Math.round(totalDailyLoad / totalDays);
    } else if (selectedTab === "Custom") {
      // Calculate average daily load for custom range
      if (customRangeData.length === 0) return 0;
      return Math.round(
        customRangeData.reduce((sum, d) => sum + d.totalLoad, 0) /
          customRangeData.length,
      );
    }
    return 0;
  }, [
    selectedTab,
    currentDayTraining,
    currentDayPost?.activityLogs,
    trainingData,
    monthlyData,
    quarterlyData,
    customRangeData,
  ]);

  // Group monthly data into 4 weekly chunks for monthly view (7-day rolling windows)
  const weeklyGroupedData = useMemo(() => {
    if (monthlyData.length !== 28) return []; // Ensure we have exactly 4 weeks

    // Group into 4 weeks (7 days each, rolling windows)
    const weeks = [];
    for (let i = 0; i < 4; i++) {
      const startIndex = i * 7;
      const weekData = monthlyData.slice(startIndex, startIndex + 7);

      if (weekData.length === 7) {
        const weekTotal = weekData.reduce((sum, day) => sum + day.totalLoad, 0);
        const weekSessions = weekData.reduce(
          (sum, day) => sum + day.sessionCount,
          0,
        );
        const validRPEs = weekData.filter((day) => day.averageRPE > 0);
        const weekAvgRPE =
          validRPEs.length > 0
            ? validRPEs.reduce((sum, day) => sum + day.averageRPE, 0) /
              validRPEs.length
            : 0;
        const weekDuration = weekData.reduce(
          (sum, day) => sum + day.totalDuration,
          0,
        );
        const weekWork = weekData.reduce((sum, day) => sum + day.totalWork, 0);
        const hasWeekData = weekData.some((day) => day.hasData);

        // Create week label
        const weekLabel = `Week ${i + 1}`;

        weeks.push({
          date: `week${i}`,
          dateLabel: weekLabel,
          totalLoad: weekTotal,
          sessionCount: weekSessions,
          averageRPE: weekAvgRPE,
          totalDuration: weekDuration,
          totalWork: weekWork,
          hasData: hasWeekData,
          weekData: weekData, // Keep individual days for tooltip
        });
      }
    }

    return weeks;
  }, [monthlyData]);

  // Group monthly data into 4 weekly chunks for the specific Weekly Averages graph (reuse monthlyData)
  const last28DaysGroupedData = useMemo(() => {
    if (monthlyData.length < 56) return []; // Ensure we have at least 56 days (28 recent + 28 previous)

    // Get the last 28 days from monthlyData (most recent data)
    // monthlyData is ordered from oldest to newest (days 0-27 are oldest, days 28-55 are most recent)
    const last28Days = monthlyData.slice(28, 56);

    // Group into 4 weeks (7 days each) - use only last 28 days
    const weeks = [];
    for (let i = 0; i < 4; i++) {
      const startIndex = i * 7;
      const weekData = last28Days.slice(startIndex, startIndex + 7);

      if (weekData.length === 7) {
        const weekTotal = weekData.reduce((sum, day) => sum + day.totalLoad, 0);
        const weekSessions = weekData.reduce(
          (sum, day) => sum + day.sessionCount,
          0,
        );
        const validRPEs = weekData.filter((day) => day.averageRPE > 0);
        const weekAvgRPE =
          validRPEs.length > 0
            ? validRPEs.reduce((sum, day) => sum + day.averageRPE, 0) /
              validRPEs.length
            : 0;
        const weekDuration = weekData.reduce(
          (sum, day) => sum + day.totalDuration,
          0,
        );
        const weekWork = weekData.reduce((sum, day) => sum + day.totalWork, 0);
        const hasWeekData = weekData.some((day) => day.hasData);

        // Get workload factor from the last day of the week (most recent)
        const lastDayWithWF = [...weekData]
          .reverse()
          .find((d) => d.workloadFactor !== undefined);
        const workloadFactor = lastDayWithWF?.workloadFactor;

        // Create week label with date range (e.g., "Dec 1-7")
        // weekData[0] is the first day, weekData[6] is the last day
        const firstDay = weekData[0].dateLabel; // Already formatted as "Dec 1"
        const lastDay = weekData[6].dateLabel; // Already formatted as "Dec 7"

        // Extract just the day number from the last day (e.g., "7" from "Dec 7")
        const lastDayNumber = lastDay.split(" ")[1];
        const weekLabel = `${firstDay}-${lastDayNumber}`;

        weeks.push({
          date: `week${i}`,
          dateLabel: weekLabel,
          totalLoad: weekTotal,
          sessionCount: weekSessions,
          averageRPE: weekAvgRPE,
          totalDuration: weekDuration,
          totalWork: weekWork,
          hasData: hasWeekData,
          workloadFactor: workloadFactor,
          weekData: weekData, // Keep individual days for tooltip
        });
      }
    }

    return weeks;
  }, [monthlyData]);

  // Calculate 7-day and 28-day averages for reference lines
  const trainingAverageLines = useMemo(() => {
    // Always use the same calculation logic regardless of view
    // Include ALL days (treat no training as 0) for true averages

    // Calculate 7-day average (daily) - include all 7 days
    const sevenDayAvg =
      trainingData.length > 0
        ? trainingData.reduce((sum, d) => sum + d.totalLoad, 0) /
          trainingData.length
        : 0;

    // Calculate previous 7-day average (days 8-14) - for comparison
    const previous7DayAvg =
      monthlyData.length >= 14
        ? monthlyData.slice(7, 14).reduce((sum, d) => sum + d.totalLoad, 0) / 7
        : 0;

    // Calculate 28-day average (daily) - include last 28 days only (most recent)
    const twentyEightDayAvg =
      monthlyData.length >= 56
        ? monthlyData.slice(28, 56).reduce((sum, d) => sum + d.totalLoad, 0) /
          28
        : 0;

    // Calculate previous 28-day average (days 1-28) - for comparison in monthly view
    const previous28DayAvg =
      monthlyData.length >= 56
        ? monthlyData.slice(0, 28).reduce((sum, d) => sum + d.totalLoad, 0) / 28
        : 0;

    return {
      sevenDay: sevenDayAvg,
      previous7Day: previous7DayAvg,
      twentyEightDay: twentyEightDayAvg,
      previous28Day: previous28DayAvg,
    };
  }, [trainingData, monthlyData]);

  // Use the same average calculations as trainingAverageLines for consistency
  const last7DaysAverageLines = useMemo(() => {
    return trainingAverageLines;
  }, [trainingAverageLines]);

  // Transform data for line chart
  const transformTrainingDataForLineChart = useMemo(() => {
    if (selectedTab === "Day") return [];

    if (selectedTab === "Week") {
      const data = trainingData;
      // Include ALL data points (treat no training as 0)
      const allDataPoints = data.map((d) => ({
        x: d.dateLabel,
        y: d.totalLoad,
      }));

      return [
        {
          id: "Training Load",
          data: allDataPoints,
        },
      ];
    } else {
      // Month view - use weekly averages (total load divided by 7) for the specific Weekly Averages graph
      // Include ALL weeks (treat no training as 0)
      const allDataPoints = last28DaysGroupedData.map((w) => ({
        x: w.dateLabel,
        y: Math.round((w.totalLoad / 7) * 10) / 10, // Weekly average divided by 7, rounded to 1 decimal
      }));

      return [
        {
          id: "Weekly Average",
          data: allDataPoints,
        },
      ];
    }
  }, [selectedTab, trainingData, last28DaysGroupedData]);

  // Calculate linked activity totals for day view
  const linkedActivityTotals = useMemo(() => {
    if (!currentDayPost) return null;

    const linkedActivities = currentDayTraining
      .map((training) =>
        getLinkedActivityForTraining(training, currentDayPost.activityLogs),
      )
      .filter(
        (activity): activity is ActivityLog & { id: string } =>
          activity !== null,
      );

    if (linkedActivities.length === 0) return null;

    return {
      totalDuration: linkedActivities.reduce(
        (sum, activity) => sum + (activity.duration || 0),
        0,
      ),
      totalCalories: linkedActivities.reduce(
        (sum, activity) => sum + (activity.calories || 0),
        0,
      ),
      totalWork: linkedActivities.reduce(
        (sum, activity) => sum + (activity.workKilojoules || 0),
        0,
      ),
      averageHR:
        linkedActivities.filter((a) => a && a.aveHR).length > 0
          ? linkedActivities
              .filter((a) => a && a.aveHR)
              .reduce((sum, activity) => sum + (activity.aveHR || 0), 0) /
            linkedActivities.filter((a) => a && a.aveHR).length
          : 0,
      maxHR: Math.max(...linkedActivities.map((a) => a?.maxHR || 0), 0),
      count: linkedActivities.length,
    };
  }, [currentDayTraining, currentDayPost]);

  // Canonical training type names keyed by ID so breakdowns group logs by
  // their training type (falling back to legacy tags for old logs).
  const [trainingTypeNamesById, setTrainingTypeNamesById] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    trainingTypeService
      .fetchTrainingTypes()
      .then((types) => {
        const map: Record<string, string> = {};
        types.forEach((t) => {
          if (t.id) map[t.id] = t.name;
        });
        setTrainingTypeNamesById(map);
      })
      .catch((err) => console.error("Error fetching training types:", err));
  }, []);

  // Calculate session type breakdown for last 7 days (Week view)
  const sessionTypeBreakdown = useMemo(() => {
    if (selectedTab !== "Week") return [];

    // Get all training logs from the last 7 days
    const allTrainingLogs = trainingData.flatMap((dayData) => {
      const dateID = dayData.date;
      const dayPost = dayPosts.find((post) => post.dateID === dateID);
      return dayPost?.trainingLogs || [];
    });

    // Group by session type (from tags) and calculate total load
    const sessionTypeMap = new Map<string, number>();

    allTrainingLogs.forEach((training) => {
      const load = training.load || 0;

      // Group by canonical training type name (tag fallback for legacy logs)
      const names = getLogTrainingTypeNames(training, trainingTypeNamesById);
      if (names.length > 0) {
        names.forEach((name) => {
          const currentLoad = sessionTypeMap.get(name) || 0;
          sessionTypeMap.set(name, currentLoad + load);
        });
      } else {
        // If no training type or tags, categorize as "Untagged"
        const currentLoad = sessionTypeMap.get("Untagged") || 0;
        sessionTypeMap.set("Untagged", currentLoad + load);
      }
    });

    // Convert to array and sort by load (descending)
    return Array.from(sessionTypeMap.entries())
      .map(([sessionType, totalLoad]) => ({
        sessionType,
        totalLoad,
      }))
      .sort((a, b) => b.totalLoad - a.totalLoad);
  }, [selectedTab, trainingData, dayPosts, trainingTypeNamesById]);

  // Calculate session type breakdown for last 28 days (Month view)
  const monthlySessionTypeBreakdown = useMemo(() => {
    if (selectedTab !== "Month") return [];

    // Get the last 28 days from monthlyData (most recent data)
    // monthlyData has 56 days: days 0-27 are oldest, days 28-55 are most recent
    const last28Days =
      monthlyData.length >= 56 ? monthlyData.slice(28, 56) : [];

    // Get all training logs from the last 28 days
    const allTrainingLogs = last28Days.flatMap((dayData) => {
      const dateID = dayData.date;
      const dayPost = dayPosts.find((post) => post.dateID === dateID);
      return dayPost?.trainingLogs || [];
    });

    // Group by session type (from tags) and calculate total load
    const sessionTypeMap = new Map<string, number>();

    allTrainingLogs.forEach((training) => {
      const load = training.load || 0;

      // Group by canonical training type name (tag fallback for legacy logs)
      const names = getLogTrainingTypeNames(training, trainingTypeNamesById);
      if (names.length > 0) {
        names.forEach((name) => {
          const currentLoad = sessionTypeMap.get(name) || 0;
          sessionTypeMap.set(name, currentLoad + load);
        });
      } else {
        // If no training type or tags, categorize as "Untagged"
        const currentLoad = sessionTypeMap.get("Untagged") || 0;
        sessionTypeMap.set("Untagged", currentLoad + load);
      }
    });

    // Convert to array and sort by load (descending)
    return Array.from(sessionTypeMap.entries())
      .map(([sessionType, totalLoad]) => ({
        sessionType,
        totalLoad,
      }))
      .sort((a, b) => b.totalLoad - a.totalLoad);
  }, [selectedTab, monthlyData, dayPosts, trainingTypeNamesById]);

  // Calculate session type breakdown for last 12 weeks (Quarter view)
  const quarterlySessionTypeBreakdown = useMemo(() => {
    if (selectedTab !== "Quarter") return [];

    // Generate the past 12 weeks of dates (84 days total)
    const quarterWeeks = generatePast12Weeks(targetDate);

    // Get all training logs from the last 12 weeks
    const allTrainingLogs = quarterWeeks.flatMap((weekDates) => {
      return weekDates.flatMap((date) => {
        const dateID = generateDateID(date);
        const dayPost = dayPosts.find((post) => post.dateID === dateID);
        return dayPost?.trainingLogs || [];
      });
    });

    // Group by session type (from tags) and calculate total load
    const sessionTypeMap = new Map<string, number>();

    allTrainingLogs.forEach((training) => {
      const load = training.load || 0;

      // Group by canonical training type name (tag fallback for legacy logs)
      const names = getLogTrainingTypeNames(training, trainingTypeNamesById);
      if (names.length > 0) {
        names.forEach((name) => {
          const currentLoad = sessionTypeMap.get(name) || 0;
          sessionTypeMap.set(name, currentLoad + load);
        });
      } else {
        // If no training type or tags, categorize as "Untagged"
        const currentLoad = sessionTypeMap.get("Untagged") || 0;
        sessionTypeMap.set("Untagged", currentLoad + load);
      }
    });

    // Convert to array and sort by load (descending)
    return Array.from(sessionTypeMap.entries())
      .map(([sessionType, totalLoad]) => ({
        sessionType,
        totalLoad,
      }))
      .sort((a, b) => b.totalLoad - a.totalLoad);
  }, [selectedTab, targetDate, dayPosts, trainingTypeNamesById]);

  // Calculate session type breakdown for custom date range
  const customRangeSessionTypeBreakdown = useMemo(() => {
    if (selectedTab !== "Custom" || customRangeData.length === 0) return [];

    // Get all training logs from the custom date range
    const allTrainingLogs = customRangeData.flatMap((dayData) => {
      const dateID = dayData.date;
      const dayPost = dayPosts.find((post) => post.dateID === dateID);
      return dayPost?.trainingLogs || [];
    });

    // Group by session type (from tags) and calculate total load
    const sessionTypeMap = new Map<string, number>();

    allTrainingLogs.forEach((training) => {
      const load = training.load || 0;

      // Group by canonical training type name (tag fallback for legacy logs)
      const names = getLogTrainingTypeNames(training, trainingTypeNamesById);
      if (names.length > 0) {
        names.forEach((name) => {
          const currentLoad = sessionTypeMap.get(name) || 0;
          sessionTypeMap.set(name, currentLoad + load);
        });
      } else {
        // If no training type or tags, categorize as "Untagged"
        const currentLoad = sessionTypeMap.get("Untagged") || 0;
        sessionTypeMap.set("Untagged", currentLoad + load);
      }
    });

    // Convert to array and sort by load (descending)
    return Array.from(sessionTypeMap.entries())
      .map(([sessionType, totalLoad]) => ({
        sessionType,
        totalLoad,
      }))
      .sort((a, b) => b.totalLoad - a.totalLoad);
  }, [selectedTab, customRangeData, dayPosts, trainingTypeNamesById]);

  // Fetch custom date range data
  const fetchCustomRangeData = async (startDate: string, endDate: string) => {
    if (!startDate || !endDate) {
      console.log("Missing start or end date");
      return;
    }

    const effectiveUserId = queryUid || currentUser?.uid;
    if (!effectiveUserId) {
      console.log("No effective user ID");
      return;
    }

    console.log("Fetching custom range data:", {
      startDate,
      endDate,
      effectiveUserId,
    });
    setCustomRangeLoading(true);
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);

      console.log("Date objects:", { start, end });

      // Generate all dates in the range
      const dates: Date[] = [];
      const currentDate = new Date(start);
      while (currentDate <= end) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }

      console.log(`Generated ${dates.length} dates in range`);

      // Fetch data for each date
      const promises = dates.map(async (date) => {
        const dateID = generateDateID(date);
        const dateLabel = date.toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
        });

        try {
          const dayPost = await getDayPost(effectiveUserId, dateID);
          const metrics = calculateTrainingMetrics(
            dayPost?.trainingLogs || [],
            dayPost?.activityLogs || [],
          );
          return {
            date: dateID,
            dateLabel,
            ...metrics,
            hasData: dayPost !== null && dayPost.trainingLogs.length > 0,
          };
        } catch (error) {
          return {
            date: dateID,
            dateLabel,
            totalLoad: 0,
            sessionCount: 0,
            averageRPE: 0,
            totalDuration: 0,
            totalWork: 0,
            hasData: false,
          };
        }
      });

      const results = await Promise.all(promises);
      // console.log("Fetched custom range data:", results);
      setCustomRangeData(results);
    } catch (error) {
      console.error("Error fetching custom range data:", error);
    } finally {
      setCustomRangeLoading(false);
    }
  };

  // Debug: Log when customRangeData changes
  useEffect(() => {
    if (customRangeData.length > 0) {
      console.log("Custom range data updated:", customRangeData);
      console.log("Selected tab:", selectedTab);
    }
  }, [customRangeData, selectedTab]);

  // Handle back navigation - return to dashboard with the same date
  const handleBack = () => {
    const dateID = generateDateID(targetDate);
    // navigate(`/dashboard?date=${dateID}`);
    navigate(-1);
  };

  return (
    <div className="w-full min-h-screen bg-base-200 pt-16">
      <Header />
      {showPremiumModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-20 p-4">
          <div className="relative">
            <button
              onClick={() => setShowPremiumModal(false)}
              className="absolute -top-3 -right-3 bg-base-100 rounded-full px-2 py-1 text-sm"
            >
              ✕
            </button>

            <ComingSoon featureKey="advancedGraphs" />
          </div>
        </div>
      )}
      {/* Header */}
      <div className="shadow-sm">
        <div className="mx-auto max-w-4xl px-4 py-4">
          <div className="flex w-full items-center justify-between">
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleBack}
                  className="p-2 rounded-full hover:bg-base-200 transition-colors bg-base-300"
                >
                  <ChevronLeftIcon className="h-5 w-5 text-base-content/80" />
                </button>
                <h1 className="text-2xl font-grotesk font-semibold text-base-content">
                  Training Load
                </h1>
              </div>
              <div className="flex space-x-2">
                {/* Export button hidden - feature not available yet */}
                {/* <button
                  onClick={() =>
                    isPremium
                      ? setShowExportDialog(true)
                      : setShowPremiumModal(true)
                  }
                  className="w-8 h-8 rounded-full bg-base-300 hover:bg-base-300 flex items-center justify-center text-base-content/70 hover:text-base-content transition-colors"
                  title="Export wellness data"
                >
                  <ArrowDownTrayIcon
                    className={`h-4 w-4 ${
                      isPremium ? "text-base-content/60" : "text-blue-500"
                    }`}
                  />
                </button> */}
                <button
                  onClick={() => setShowInfoDialog(true)}
                  className="w-8 h-8 rounded-full bg-base-300 hover:bg-base-300 flex items-center justify-center text-base-content/70 hover:text-base-content transition-colors"
                  title="Learn more about Health and Sleep"
                >
                  ?
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cache/Offline Status Indicators - similar to Dashboard */}
      {isOffline && (
        <div className="bg-orange-500/10 border-orange-200 border p-3 mb-4 mx-auto max-w-4xl">
          <div className="flex items-center space-x-2 text-orange-700">
            <BellIcon className="h-5 w-5" />
            <span className="text-sm font-medium">
              You're currently offline. Showing cached data.
            </span>
          </div>
        </div>
      )}

      {/* Admin viewing indicator */}
      {queryUid && viewedUser && (
        <div className="bg-blue-500/10 border-blue-200 border p-3 mb-4 mx-auto max-w-4xl">
          <div className="flex items-center space-x-2 text-blue-700">
            <EyeIcon className="h-5 w-5" />
            <span className="text-sm font-medium">
              Viewing {viewedUser.username}'s training insights
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      {/* Tab Navigation */}
      <div className="mx-auto max-w-4xl">
        <div className="flex justify-around border-b border-base-300">
          {(["Day", "Week", "Month", "Quarter"] as const).map((tab) => {
            // Determine if this tab is restricted for the current user
            const isRestricted =
              (tab === "Month" && !hasMonthlyAccess) ||
              (tab === "Quarter" && !hasQuarterlyAccess);

            return (
              <button
                key={tab}
                onClick={() => {
                  if (isRestricted) {
                    setShowPremiumModal(true);
                  } else {
                    setSelectedTab(tab);
                  }
                }}
                className={`relative py-3 px-4 text-sm font-medium font-grotesk transition-colors duration-200 flex-1 text-center flex items-center justify-center gap-1 ${
                  selectedTab === tab
                    ? "text-base-content border-b-2 border-base-content bg-base-300"
                    : "text-base-content/60 hover:text-base-content/80"
                }`}
              >
                {tab}
                {isRestricted && (
                  <span className="text-blue-500">
                    <LockClosedIcon className="w-3 h-3" />
                  </span>
                )}
              </button>
            );
          })}
          <button
            onClick={() => {
              if (hasCustomDateAccess) {
                setShowCustomDateModal(true);
              } else {
                setShowPremiumModal(true);
              }
            }}
            className={`py-3 px-4 text-sm font-medium font-grotesk transition-colors duration-200 flex-1 text-center flex items-center justify-center gap-2 ${
              selectedTab === "Custom"
                ? "text-base-content border-b-2 border-base-content bg-base-300"
                : "text-base-content/60 hover:text-base-content/80"
            }`}
          >
            <CalendarIcon
              className={`h-5 w-5 ${
                hasCustomDateAccess ? "text-base-content/60" : "text-blue-500"
              }`}
            />
            {!hasCustomDateAccess && (
              <span className="text-blue-500">
                <LockClosedIcon className="w-3 h-3" />
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="mx-auto max-w-4xl">
          <div className="flex flex-col items-center justify-center py-20">
            <div className="flex flex-col items-center gap-6">
              {/* Large animated chevron */}
              <h1 className="text-base-content text-[8rem] font-groteskLogo animate-pulse-custom relative z-10">
                ^
              </h1>

              {/* Loading text */}
              <div className="text-center -mt-16">
                <p className="text-lg font-medium text-base-content font-grotesk">
                  Loading Training data...
                </p>
                <p className="text-sm text-base-content/70 font-grotesk mt-2">
                  Analyzing your training metrics
                </p>
              </div>

              {/* Loading progress indicator */}
              <div className="flex items-center gap-2 mt-4">
                <div className="w-32 h-1 bg-base-300 rounded-full overflow-hidden">
                  <div className="h-full bg-base-200 rounded-full animate-pulse transition-all duration-1000 w-3/4"></div>
                </div>
                <div className="w-2 h-2 bg-base-200 rounded-full animate-ping"></div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mx-auto max-w-4xl">
            {/* Main Score Display - hide for restricted views */}
            <div className="mb-2 p-4 px-8 pb-0">
              <div
                className={
                  selectedTab === "Quarter" || selectedTab === "Month"
                    ? "relative"
                    : ""
                }
              >
                {/* Show Premium Modal trigger for Quarter if restricted */}
                {!hasQuarterlyAccess && selectedTab === "Quarter" && (
                  <div className="absolute inset-0 z-10 flex items-start justify-center pt-20">
                    <ComingSoon featureKey="advancedGraphs" />
                  </div>
                )}
                {/* Monthly is now unlocked for Plus, so we remove the !hasMonthlyAccess check here */}
                <div
                  className={
                    !hasQuarterlyAccess && selectedTab === "Quarter"
                      ? "blur-md pointer-events-none"
                      : "flex items-center justify-between"
                  }
                >
                  <div>
                    {selectedTab === "Day" && (
                      <>
                        <div className="text-6xl font-bold font-heading text-base-content">
                          {Math.round(averageLoad)}
                          <span className="text-lg text-base-content/50 ml-2">
                            load
                          </span>
                        </div>
                        <div className="text-lg text-base-content/70 font-grotesk mb-1">
                          Today
                        </div>
                      </>
                    )}
                    {selectedTab === "Custom" && (
                      <>
                        <div className="text-6xl font-bold font-heading text-base-content">
                          {Math.round(averageLoad)}
                          <span className="text-lg text-base-content/50 ml-2">
                            load
                          </span>
                        </div>
                        <div className="text-lg text-base-content/70 font-grotesk mb-1">
                          Daily Average
                        </div>
                      </>
                    )}
                    {/* <div className="text-sm text-base-content/60">
                {selectedTab === "Day" &&
                  "Based on your training sessions today"}
                {selectedTab === "Week" &&
                  "Higher loads indicate increased training volume and intensity"}
                {selectedTab === "Month" &&
                  "Based on your daily training patterns over the past 30 days"}
              </div> */}
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Chart body area */}
            <div className="">
              {selectedTab === "Day" ? (
                // Day view - detailed training sessions
                <div className="pt-8 pb-8">
                  {/* Load Balance Display */}
                  {dayLoadBalance !== undefined && (
                    <div>
                      <div className="flex justify-between px-8">
                        <div className="font-grotesk mb-4">
                          <p>
                            <strong>Load Balance</strong>
                          </p>
                          <p className="text-xs">
                            How your workload is progressing
                          </p>
                        </div>
                        <div className="flex justify-between items-start">
                          <span
                            className="text-2xl"
                            style={{ color: "oklch(var(--bc))" }}
                          >
                            {(() => {
                              const wfForDisplay = dayLoadBalance;
                              const diff = Math.round(100 - wfForDisplay * 100);
                              return `${diff > 0 ? "+" : ""}${diff}%`;
                            })()}
                          </span>
                        </div>
                      </div>
                      <div className="mx-8 bg-base-100 rounded-lg shadow-sm p-6">
                        <div className="text-sm text-base-content/80">
                          {(() => {
                            const wf = dayLoadBalance;
                            if (wf < 0.75) {
                              return (
                                <>
                                  <p className="font-bold font-grotesk text-base-content mb-1 text-lg">
                                    Sharp Load Spike / High Stress
                                  </p>
                                  <p>
                                    Recent training is much higher than what
                                    your body has been prepared for. Fatigue
                                    accumulates quickly and injury risk is
                                    elevated if this is sustained.
                                  </p>
                                </>
                              );
                            } else if (wf >= 0.75 && wf < 0.9) {
                              return (
                                <>
                                  <p className="font-bold font-grotesk text-base-content mb-1 text-lg">
                                    Elevated Load / High Challenge
                                  </p>
                                  <p>
                                    Training demand is above your recent
                                    capacity. This zone can drive adaptation,
                                    but recovery quality becomes critical.
                                  </p>
                                </>
                              );
                            } else if (wf >= 0.9 && wf < 1.0) {
                              return (
                                <>
                                  <p className="font-bold font-grotesk text-base-content mb-1 text-lg">
                                    Optimal Load / Building Phase
                                  </p>
                                  <p>
                                    Recent training is slightly higher than your
                                    established capacity. This is an ideal zone
                                    for building fitness and progressive
                                    overload.
                                  </p>
                                </>
                              );
                            } else if (wf >= 1.0 && wf < 1.1) {
                              return (
                                <>
                                  <p className="font-bold font-grotesk text-base-content mb-1 text-lg">
                                    Optimal Load / Maintenance or Sharpening
                                  </p>
                                  <p>
                                    Recent training closely matches your
                                    established capacity. This supports
                                    consistency, performance stability, and
                                    readiness.
                                  </p>
                                </>
                              );
                            } else if (wf >= 1.1 && wf < 1.25) {
                              return (
                                <>
                                  <p className="font-bold font-grotesk text-base-content mb-1 text-lg">
                                    Reduced Load / Recovery or Taper
                                  </p>
                                  <p>
                                    Training demand is lower than what you are
                                    conditioned for. Useful for deloads,
                                    recovery blocks, or pre-competition
                                    tapering.
                                  </p>
                                </>
                              );
                            } else {
                              return (
                                <>
                                  <p className="font-bold font-grotesk text-base-content mb-1 text-lg">
                                    Underloading / Deconditioning Risk
                                  </p>
                                  <p>
                                    Training load is well below your capacity.
                                    If prolonged, fitness may decline and
                                    performance readiness can drop.
                                  </p>
                                </>
                              );
                            }
                          })()}
                        </div>
                      </div>
                    </div>
                  )}

                  <p className="pl-8 font-grotesk mb-4 pt-8">
                    <strong>Training</strong> Sessions
                  </p>
                  {currentDayTraining.length > 0 ? (
                    <div className="space-y-4 mx-8">
                      {currentDayTraining.map((session, index) => {
                        const linkedActivity = currentDayPost
                          ? getLinkedActivityForTraining(
                              session,
                              currentDayPost.activityLogs,
                            )
                          : null;

                        return (
                          <TrainingSessionCard
                            key={session.id || index}
                            trainingLog={session}
                            linkedActivity={linkedActivity}
                            onClick={goToTrainingLogInsights}
                            onEdit={
                              !queryUid ? handleEditTrainingLog : undefined
                            }
                            onDelete={
                              !queryUid ? handleDeleteTrainingLog : undefined
                            }
                            className=""
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-left p-8">
                      <p className="text-base-content/60 text-sm">
                        No training sessions recorded yet.
                      </p>
                    </div>
                  )}
                </div>
              ) : // Week, Month, Quarter, and Custom view - charts
              !loading &&
                ((selectedTab === "Week" && trainingData.length > 0) ||
                  (selectedTab === "Month" && monthlyData.length > 0) ||
                  (selectedTab === "Quarter" && quarterlyData.length > 0) ||
                  (selectedTab === "Custom" && customRangeData.length > 0)) ? (
                <div className="space-y-8">
                  {/* Week View - No extra spacing needed */}
                  {selectedTab === "Week" ? (
                    <></>
                  ) : selectedTab === "Month" ? (
                    // Month view - line chart first, then bars
                    <div className="space-y-8 p-8 pt-0">
                      {/* Weekly Average Training Load Trend for Month view */}
                      {transformTrainingDataForLineChart.length > 0 && (
                        <div
                          className={
                            !hasMonthlyAccess
                              ? "blur-md pointer-events-none"
                              : ""
                          }
                        >
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <h3 className="text-xl font-heading font-semibold text-base-content">
                                Last 28 Days
                              </h3>
                              <p className="text-xs text-base-content/60">
                                {monthViewMode === "daily"
                                  ? "Daily Training Load"
                                  : "Weekly Average Load"}
                              </p>
                            </div>
                            {/* Toggle switch for daily/weekly view */}
                            <div className="flex items-center gap-3 bg-base-200 rounded-lg p-1">
                              <button
                                onClick={() => setMonthViewMode("daily")}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                                  monthViewMode === "daily"
                                    ? "bg-base-100 text-base-content shadow-sm"
                                    : "text-base-content/70 hover:text-base-content"
                                }`}
                              >
                                Daily
                              </button>
                              <button
                                onClick={() => setMonthViewMode("weekly")}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                                  monthViewMode === "weekly"
                                    ? "bg-base-100 text-base-content shadow-sm"
                                    : "text-base-content/70 hover:text-base-content"
                                }`}
                              >
                                Weekly
                              </button>
                            </div>
                          </div>
                          <div className="h-80 w-full">
                            <ResponsiveLine
                              theme={nivoTheme}
                              data={[
                                {
                                  id: "monthlyLoad",
                                  data:
                                    monthViewMode === "daily"
                                      ? monthlyData.slice(28, 56).map((d) => ({
                                          x: d.dateLabel,
                                          y: d.totalLoad,
                                        }))
                                      : last28DaysGroupedData.map((d) => ({
                                          x: d.dateLabel,
                                          y: d.totalLoad / 7, // Weekly average
                                        })),
                                },
                              ]}
                              margin={{
                                top: 40,
                                right: monthViewMode === "weekly" ? 80 : 20,
                                bottom: 100,
                                left: monthViewMode === "weekly" ? 80 : 40,
                              }}
                              xScale={{ type: "point" }}
                              yScale={{
                                type: "linear",
                                min: 0,
                                max:
                                  monthViewMode === "daily"
                                    ? Math.max(
                                        ...monthlyData
                                          .slice(28, 56)
                                          .map((d) => d.totalLoad || 0),
                                      ) * 1.1 || 100
                                    : Math.max(
                                        ...last28DaysGroupedData.map(
                                          (d) => d.totalLoad / 7 || 0,
                                        ),
                                      ) * 1.1 || 100,
                                stacked: false,
                              }}
                              axisTop={null}
                              axisRight={{
                                tickSize: 0,
                                tickPadding: 10,
                                tickRotation: 0,
                                legend: "",
                                legendPosition: "middle",
                                legendOffset: 45,
                                tickValues: [],
                                format: (value: any) => "",
                              }}
                              axisBottom={
                                isMobile
                                  ? null
                                  : {
                                      tickSize: 5,
                                      tickPadding: 5,
                                      tickRotation: -45,
                                      legend: "",
                                      legendPosition: "middle",
                                      legendOffset: 50,
                                      tickValues:
                                        monthViewMode === "daily"
                                          ? monthlyData
                                              .slice(28, 56)
                                              .map((d) => d.dateLabel)
                                          : last28DaysGroupedData.map(
                                              (d) => d.dateLabel,
                                            ),
                                    }
                              }
                              axisLeft={{
                                tickSize: 0,
                                tickPadding: 10,
                                tickRotation: 0,
                                legend: "",
                                legendPosition: "middle",
                                legendOffset: -40,
                                tickValues: 5,
                                format: (value: any) =>
                                  Math.round(value).toString(),
                              }}
                              enableGridX={false}
                              enableGridY={true}
                              enablePoints={false}
                              enableArea={false}
                              useMesh={true}
                              enableSlices="x"
                              onClick={(point: any) => {
                                // When clicking on the chart, pin the current slice
                                if (hoveredSlice) {
                                  setPinnedSlice(hoveredSlice);
                                }
                              }}
                              onMouseLeave={() => {
                                setHoveredSlice(null);
                              }}
                              sliceTooltip={({ slice }: any) => {
                                // Use setTimeout to update state after render
                                setTimeout(() => setHoveredSlice(slice), 0);
                                return null;
                              }}
                              layers={[
                                "grid",
                                "markers",
                                "axes",
                                // Custom layer for training load bars (same style as Week view)
                                ({ xScale, yScale, innerWidth }: any) => {
                                  const chartData =
                                    monthViewMode === "daily"
                                      ? monthlyData.slice(28, 56)
                                      : last28DaysGroupedData;
                                  const maxLoad =
                                    monthViewMode === "daily"
                                      ? Math.max(
                                          ...chartData.map(
                                            (d) => d.totalLoad || 0,
                                          ),
                                        )
                                      : Math.max(
                                          ...chartData.map(
                                            (d) => d.totalLoad / 7 || 0,
                                          ),
                                        );

                                  if (maxLoad === 0) return null;

                                  // Responsive bar width - narrower on mobile/tablet for daily, wider for weekly
                                  const barWidth =
                                    monthViewMode === "daily"
                                      ? innerWidth < 600
                                        ? 8
                                        : innerWidth < 900
                                          ? 16
                                          : 20
                                      : innerWidth < 600
                                        ? 40
                                        : 60;

                                  // Get all x positions to calculate offset
                                  const allXPositions = chartData.map((d) =>
                                    xScale(d.dateLabel),
                                  );

                                  // Calculate offset to ensure bars don't get clipped
                                  const firstBarX =
                                    allXPositions[0] - barWidth / 2;
                                  const offset =
                                    firstBarX < 0 ? Math.abs(firstBarX) + 2 : 0;

                                  return (
                                    <g key="training-load-bars">
                                      {chartData.map((dataPoint, index) => {
                                        const xPos = xScale(
                                          dataPoint.dateLabel,
                                        );
                                        const barX =
                                          xPos - barWidth / 2 + offset;

                                        // Scale bar height relative to max load
                                        const loadValue =
                                          monthViewMode === "daily"
                                            ? dataPoint.totalLoad || 0
                                            : dataPoint.totalLoad / 7 || 0;
                                        const maxYValue =
                                          monthViewMode === "daily"
                                            ? Math.max(
                                                ...chartData.map(
                                                  (d) => d.totalLoad || 0,
                                                ),
                                              ) * 1.1 || 100
                                            : Math.max(
                                                ...chartData.map(
                                                  (d) => d.totalLoad / 7 || 0,
                                                ),
                                              ) * 1.1 || 100;
                                        const barValue =
                                          maxLoad > 0
                                            ? (loadValue / maxLoad) * maxYValue
                                            : 0;
                                        const barHeight =
                                          yScale(0) - yScale(barValue);

                                        return (
                                          <g key={`bar-group-${index}`}>
                                            {/* Bar */}
                                            <rect
                                              key={`bar-${index}`}
                                              x={barX}
                                              y={yScale(barValue)}
                                              width={barWidth}
                                              height={barHeight}
                                              fill={
                                                dataPoint.hasData
                                                  ? "#9ca3af"
                                                  : "#e5e7eb"
                                              }
                                              opacity={0.6}
                                            />
                                            {/* Load value label on top of bar - hidden on mobile for daily view */}
                                            {loadValue > 0 &&
                                              (monthViewMode === "weekly" ||
                                                innerWidth >= 600) && (
                                                <text
                                                  key={`label-${index}`}
                                                  x={xPos + offset}
                                                  y={yScale(barValue) - 5}
                                                  textAnchor="middle"
                                                  fontSize="11"
                                                  fill={
                                                    isDark
                                                      ? "#ffffff"
                                                      : "#374151"
                                                  }
                                                  fontWeight="500"
                                                >
                                                  {Math.round(loadValue)}
                                                </text>
                                              )}
                                          </g>
                                        );
                                      })}
                                    </g>
                                  );
                                },
                                // Custom layer for average lines
                                ({ xScale, yScale, width }: any) => {
                                  const chartData =
                                    monthViewMode === "daily"
                                      ? monthlyData.slice(28, 56)
                                      : last28DaysGroupedData;
                                  // Calculate the x-position of the first and last data points
                                  const firstX =
                                    chartData.length > 0
                                      ? xScale(chartData[0].dateLabel)
                                      : 0;
                                  const lastX =
                                    chartData.length > 0
                                      ? xScale(
                                          chartData[chartData.length - 1]
                                            .dateLabel,
                                        )
                                      : width;

                                  return (
                                    <g key="average-lines">
                                      {/* 7-Day Average Line (Purple) */}
                                      {show7DayAverage &&
                                        last7DaysAverageLines.sevenDay > 0 && (
                                          <g>
                                            <line
                                              x1={firstX}
                                              y1={yScale(
                                                last7DaysAverageLines.sevenDay,
                                              )}
                                              x2={lastX}
                                              y2={yScale(
                                                last7DaysAverageLines.sevenDay,
                                              )}
                                              stroke="#7c3aed"
                                              strokeWidth={2}
                                              strokeDasharray="8,4"
                                            />
                                          </g>
                                        )}
                                      {/* 28-Day Average Line (Orange) */}
                                      {show28DayAverage &&
                                        last7DaysAverageLines.twentyEightDay >
                                          0 && (
                                          <g>
                                            <line
                                              x1={firstX}
                                              y1={yScale(
                                                last7DaysAverageLines.twentyEightDay,
                                              )}
                                              x2={lastX}
                                              y2={yScale(
                                                last7DaysAverageLines.twentyEightDay,
                                              )}
                                              stroke="#f97316"
                                              strokeWidth={2}
                                              strokeDasharray="8,4"
                                            />
                                          </g>
                                        )}
                                      {/* Previous 28-Day Average Line (Gray) */}
                                      {showPrevious28DayAverage &&
                                        last7DaysAverageLines.previous28Day >
                                          0 && (
                                          <g>
                                            <line
                                              x1={firstX}
                                              y1={yScale(
                                                last7DaysAverageLines.previous28Day,
                                              )}
                                              x2={lastX}
                                              y2={yScale(
                                                last7DaysAverageLines.previous28Day,
                                              )}
                                              stroke="#6b7280"
                                              strokeWidth={2}
                                              strokeDasharray="8,4"
                                            />
                                          </g>
                                        )}
                                    </g>
                                  );
                                },
                                // Custom layer for workload factor line (secondary y-axis)
                                ({ xScale, innerHeight, innerWidth }: any) => {
                                  const chartData =
                                    monthViewMode === "daily"
                                      ? monthlyData.slice(28, 56)
                                      : last28DaysGroupedData;
                                  // Create a custom y-scale for workload factor (30% to 150%) - inverted so lower wf (overloading) plots higher
                                  const wfYScale = (value: number) => {
                                    const min = 0.3;
                                    const max = 1.5;
                                    const normalized =
                                      (value - min) / (max - min);
                                    return normalized * innerHeight;
                                  };

                                  // Calculate the same offset used for bars - responsive width
                                  const barWidth =
                                    monthViewMode === "daily"
                                      ? innerWidth < 600
                                        ? 8
                                        : innerWidth < 900
                                          ? 16
                                          : 20
                                      : innerWidth < 600
                                        ? 40
                                        : 60;
                                  const allXPositions = chartData.map((d) =>
                                    xScale(d.dateLabel),
                                  );
                                  const firstBarX =
                                    allXPositions[0] - barWidth / 2;
                                  const offset =
                                    firstBarX < 0 ? Math.abs(firstBarX) + 2 : 0;

                                  // Filter data points that have workload factor
                                  const wfDataPoints = chartData
                                    .map((d) => ({
                                      x: xScale(d.dateLabel) + offset,
                                      y: d.workloadFactor
                                        ? wfYScale(d.workloadFactor)
                                        : null,
                                      value: d.workloadFactor,
                                      label: d.dateLabel,
                                    }))
                                    .filter(
                                      (
                                        d,
                                      ): d is {
                                        x: number;
                                        y: number;
                                        value: number;
                                        label: string;
                                      } =>
                                        d.y !== null && d.value !== undefined,
                                    );

                                  if (wfDataPoints.length === 0) return null;

                                  // Create path for the line
                                  const pathData = wfDataPoints
                                    .map((d, i) =>
                                      i === 0
                                        ? `M ${d.x},${d.y}`
                                        : `L ${d.x},${d.y}`,
                                    )
                                    .join(" ");

                                  return (
                                    <g key="workload-factor-line">
                                      {/* Load Balance Line */}
                                      <path
                                        d={pathData}
                                        fill="none"
                                        stroke={isDark ? "#ffffff" : "#000000"}
                                        strokeWidth={2}
                                      />
                                      {/* Points on the line */}
                                      {wfDataPoints.map((d, i) => (
                                        <circle
                                          key={`wf-point-${i}`}
                                          cx={d.x}
                                          cy={d.y}
                                          r={4}
                                          fill={isDark ? "#ffffff" : "#000000"}
                                          stroke="white"
                                          strokeWidth={2}
                                        />
                                      ))}
                                    </g>
                                  );
                                },
                                "slices",
                              ]}
                              animate={true}
                            />
                          </div>

                          {/* Fixed metric values display */}
                          <div className=" p-4">
                            {(() => {
                              // Use pinned slice if available, otherwise use hovered slice
                              const displaySlice = hoveredSlice || pinnedSlice;

                              if (!displaySlice || !displaySlice.points) {
                                return (
                                  <div className="text-sm text-base-content/60 text-center">
                                    Hover over the graph to see values
                                  </div>
                                );
                              }

                              // Get the date label from the slice
                              const dateLabel = displaySlice.points[0]?.data?.x;

                              // Find the corresponding data point based on mode
                              const dataPoint =
                                monthViewMode === "daily"
                                  ? monthlyData
                                      .slice(28, 56)
                                      .find((d) => d.dateLabel === dateLabel)
                                  : last28DaysGroupedData.find(
                                      (d) => d.dateLabel === dateLabel,
                                    );

                              if (!dataPoint) return null;

                              return (
                                <>
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="font-medium text-base-content">
                                      {dateLabel}
                                    </div>
                                    {pinnedSlice && !hoveredSlice && (
                                      <span className="text-xs text-base-content/60 italic">
                                        (pinned)
                                      </span>
                                    )}
                                  </div>
                                  <div className="space-y-2">
                                    {/* Load Display */}
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <div
                                          className="w-4 h-4 rounded-full"
                                          style={{
                                            backgroundColor:
                                              "oklch(var(--bc) / 0.5)",
                                          }}
                                        />
                                        <span className="text-sm font-medium text-base-content/80">
                                          {monthViewMode === "daily"
                                            ? "Daily Load"
                                            : "Weekly Avg Load"}
                                        </span>
                                      </div>
                                      <span
                                        className="text-lg font-bold"
                                        style={{
                                          color: "oklch(var(--bc) / 0.5)",
                                        }}
                                      >
                                        {monthViewMode === "daily"
                                          ? Math.round(
                                              dataPoint.totalLoad * 10,
                                            ) / 10
                                          : Math.round(
                                              (dataPoint.totalLoad / 7) * 10,
                                            ) / 10}
                                      </span>
                                    </div>
                                    {/* Load Balance */}
                                    {dataPoint.workloadFactor !== undefined && (
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <div
                                            className="w-4 h-4 rounded-full"
                                            style={{
                                              backgroundColor:
                                                "oklch(var(--bc))",
                                            }}
                                          />
                                          <span className="text-sm font-medium text-base-content/80">
                                            Load Balance
                                          </span>
                                        </div>
                                        {(() => {
                                          const wf = dataPoint.workloadFactor;
                                          const diff = Math.round(
                                            100 - wf * 100,
                                          );
                                          const title =
                                            wf < 0.75
                                              ? "Sharp Load Spike / High Stress"
                                              : wf < 0.9
                                                ? "Elevated Load / High Challenge"
                                                : wf < 1.0
                                                  ? "Optimal Load / Building Phase"
                                                  : wf < 1.1
                                                    ? "Optimal Load / Maintenance or Sharpening"
                                                    : wf < 1.25
                                                      ? "Reduced Load / Recovery or Taper"
                                                      : "Underloading / Deconditioning Risk";
                                          return (
                                            <div className="flex flex-col items-end">
                                              <span
                                                className="text-lg font-bold"
                                                style={{
                                                  color: "oklch(var(--bc))",
                                                }}
                                              >{`${diff > 0 ? "+" : ""}${diff}%`}</span>
                                              <span className="text-xs text-base-content/60">
                                                {title}
                                              </span>
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    )}
                                  </div>
                                </>
                              );
                            })()}
                          </div>

                          {/* Average indicators below chart */}
                          {(last7DaysAverageLines.sevenDay > 0 ||
                            last7DaysAverageLines.twentyEightDay > 0) && (
                            <div className="">
                              {last7DaysAverageLines.twentyEightDay > 0 && (
                                <div
                                  onClick={() =>
                                    setShow28DayAverage(!show28DayAverage)
                                  }
                                  className="flex items-center gap-3 mt-2 text-sm p-2 px-4 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={show28DayAverage}
                                    onChange={() =>
                                      setShow28DayAverage(!show28DayAverage)
                                    }
                                    className="checkbox"
                                  />
                                  <span className="font-heading text-lg font-bold text-orange-600">
                                    {Math.round(
                                      last7DaysAverageLines.twentyEightDay,
                                    )}
                                  </span>
                                  <span className="font-medium text-xs text-base-content/60">
                                    28 Day Daily Average
                                  </span>
                                </div>
                              )}
                              {last7DaysAverageLines.sevenDay > 0 && (
                                <div
                                  onClick={() =>
                                    setShow7DayAverage(!show7DayAverage)
                                  }
                                  className="flex items-center gap-3 mt-2 text-sm p-2 px-4 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={show7DayAverage}
                                    onChange={() =>
                                      setShow7DayAverage(!show7DayAverage)
                                    }
                                    className="checkbox"
                                  />
                                  <span className="font-heading text-lg font-bold text-purple-600">
                                    {Math.round(last7DaysAverageLines.sevenDay)}
                                  </span>
                                  <span className="font-medium text-xs text-base-content/60">
                                    7 Day Daily Average
                                  </span>
                                </div>
                              )}

                              {/* Previous 28 Days Average - comparison value */}
                              {last7DaysAverageLines.previous28Day > 0 && (
                                <div className="mt-2 text-sm p-2 px-4">
                                  <div
                                    onClick={() =>
                                      setShowPrevious28DayAverage(
                                        !showPrevious28DayAverage,
                                      )
                                    }
                                    className="flex items-center gap-3 cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={showPrevious28DayAverage}
                                      onChange={() =>
                                        setShowPrevious28DayAverage(
                                          !showPrevious28DayAverage,
                                        )
                                      }
                                      className="checkbox"
                                    />
                                    <span className="font-heading text-lg font-bold text-base-content/80">
                                      {Math.round(
                                        last7DaysAverageLines.previous28Day,
                                      )}
                                    </span>
                                    <span className="font-medium text-xs text-base-content/60">
                                      Previous 28 Days Daily Average
                                    </span>
                                  </div>
                                  {/* Percentage difference between last 28 days and previous 28 days */}
                                  {last7DaysAverageLines.twentyEightDay > 0 && (
                                    <div className="ml-7 mt-1">
                                      {(() => {
                                        const percentDiff =
                                          ((last7DaysAverageLines.twentyEightDay -
                                            last7DaysAverageLines.previous28Day) /
                                            last7DaysAverageLines.previous28Day) *
                                          100;
                                        const isPositive = percentDiff > 0;
                                        const isNegative = percentDiff < 0;

                                        return (
                                          <div className="flex items-center gap-2">
                                            <span
                                              className={`font-heading text-xs font-bold ${
                                                isPositive
                                                  ? "text-green-600"
                                                  : isNegative
                                                    ? "text-red-600"
                                                    : "text-base-content/70"
                                              }`}
                                            >
                                              {isPositive ? "+" : ""}
                                              {percentDiff.toFixed(1)}%
                                            </span>
                                            <span className="text-base-content/50 font-medium text-xs">
                                              Last 28 days vs previous 28 days
                                            </span>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Load Breakdown for Month view */}
                      <div
                        className={`mt-8 border-t border-base-300 pt-10 ${
                          !hasMonthlyAccess ? "blur-md pointer-events-none" : ""
                        }`}
                      >
                        <h3 className="text-xl font-heading font-semibold text-base-content">
                          Load Breakdown
                        </h3>
                        <p className="text-xs text-base-content/60 mb-4">
                          By Session Type (Last 28 Days)
                        </p>
                        <div className="w-full relative">
                          {monthlySessionTypeBreakdown.length > 0 ? (
                            <>
                              <div className="space-y-3">
                                {(() => {
                                  const totalLoad =
                                    monthlySessionTypeBreakdown.reduce(
                                      (sum, st) => sum + st.totalLoad,
                                      0,
                                    );

                                  const itemsToShow = showAllMonthBreakdown
                                    ? monthlySessionTypeBreakdown
                                    : monthlySessionTypeBreakdown.slice(0, 5);

                                  return itemsToShow.map((item, index) => {
                                    const percentage =
                                      totalLoad > 0
                                        ? (item.totalLoad / totalLoad) * 100
                                        : 0;

                                    return (
                                      <div key={index} className="relative">
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-md font-medium text-base-content">
                                            {item.sessionType}
                                          </span>
                                          <span className="text-sm font-bold text-base-content">
                                            {percentage.toFixed(1)}%
                                          </span>
                                        </div>
                                        <div className="w-full bg-base-300 rounded-md h-7 overflow-hidden mb-4">
                                          <div
                                            className="bg-base-content/60 h-full rounded-md transition-all duration-500 flex items-center justify-end pr-3"
                                            style={{
                                              width: `${percentage}%`,
                                            }}
                                          >
                                            {percentage > 15 && (
                                              <span className="text-white text-xs font-bold">
                                                {percentage.toFixed(1)}%
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                              {monthlySessionTypeBreakdown.length > 5 && (
                                <button
                                  onClick={() =>
                                    setShowAllMonthBreakdown(
                                      !showAllMonthBreakdown,
                                    )
                                  }
                                  className="mt-4 w-full px-4 py-2 text-sm font-medium text-base-content/80 bg-base-200 rounded-md hover:bg-base-300 transition-colors"
                                >
                                  {showAllMonthBreakdown
                                    ? "Show Less"
                                    : `Load More (${
                                        monthlySessionTypeBreakdown.length - 5
                                      } more)`}
                                </button>
                              )}
                            </>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <p className="text-base-content/60 text-sm">
                                No load data available for the last 28 days
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : selectedTab === "Quarter" ? (
                    // Quarter view - 12 weeks of weekly training load averages
                    <div className="space-y-8  p-8 pt-0">
                      <div
                        className={
                          !hasQuarterlyAccess
                            ? "blur-md pointer-events-none"
                            : ""
                        }
                      >
                        <h3 className="text-xl font-heading font-semibold text-base-content">
                          Last 12 Weeks
                        </h3>
                        <p className="text-xs text-base-content/60 mb-4">
                          Daily Average Load
                        </p>
                        <div className="h-80 w-full">
                          <ResponsiveLine
                            theme={nivoTheme}
                            data={[
                              {
                                id: "quarterlyLoad",
                                data: quarterlyData.map((d) => ({
                                  x: d.dateLabel,
                                  y: (d.totalLoad || 0) / 7, // Use actual weekly average for slice interaction
                                })),
                              },
                            ]}
                            margin={{
                              top: 40,
                              right: 60,
                              bottom: 100,
                              left: 50,
                            }}
                            xScale={{ type: "point" }}
                            yScale={{
                              type: "linear",
                              min: 0,
                              max:
                                Math.max(
                                  ...quarterlyData.map(
                                    (d) => (d.totalLoad || 0) / 7,
                                  ),
                                ) * 1.1 || 100,
                              stacked: false,
                            }}
                            axisTop={null}
                            axisRight={{
                              tickSize: 0,
                              tickPadding: 10,
                              tickRotation: 0,
                              legend: "",
                              legendPosition: "middle",
                              legendOffset: 45,
                              tickValues: [],
                              format: (value: any) => "",
                            }}
                            axisLeft={{
                              tickSize: 0,
                              tickPadding: 10,
                              tickRotation: 0,
                              legend: "",
                              legendPosition: "middle",
                              legendOffset: -40,
                              tickValues: 5,
                              format: (value: any) =>
                                Math.round(value).toString(),
                            }}
                            axisBottom={{
                              tickSize: 5,
                              tickPadding: 5,
                              tickRotation: -45,
                              legend: "",
                              legendPosition: "middle",
                              legendOffset: 50,
                              tickValues: quarterlyData.map((d) => d.dateLabel),
                            }}
                            enableGridX={false}
                            enableGridY={true}
                            enablePoints={false}
                            enableArea={false}
                            useMesh={true}
                            enableSlices="x"
                            onClick={(point: any) => {
                              // When clicking on the chart, pin the current slice
                              if (hoveredSlice) {
                                setPinnedSlice(hoveredSlice);
                              }
                            }}
                            onMouseLeave={() => {
                              setHoveredSlice(null);
                            }}
                            sliceTooltip={({ slice }: any) => {
                              // Use setTimeout to update state after render
                              setTimeout(() => setHoveredSlice(slice), 0);
                              return null;
                            }}
                            layers={[
                              "grid",
                              "markers",
                              "axes",
                              // Custom layer for training load bars (same style as Week view)
                              ({ xScale, yScale, innerWidth }: any) => {
                                const maxAverageDailyLoad = Math.max(
                                  ...quarterlyData.map(
                                    (d) => (d.totalLoad || 0) / 7,
                                  ),
                                );

                                if (maxAverageDailyLoad === 0) return null;

                                // Responsive bar width - narrower on mobile
                                const barWidth = innerWidth < 600 ? 20 : 40;

                                // Get all x positions to calculate offset
                                const allXPositions = quarterlyData.map((d) =>
                                  xScale(d.dateLabel),
                                );

                                // Calculate offset to ensure bars don't get clipped
                                const firstBarX =
                                  allXPositions[0] - barWidth / 2;
                                const offset =
                                  firstBarX < 0 ? Math.abs(firstBarX) + 2 : 0;

                                return (
                                  <g key="training-load-bars">
                                    {quarterlyData.map((dataPoint, index) => {
                                      const xPos = xScale(dataPoint.dateLabel);
                                      const barX = xPos - barWidth / 2 + offset;
                                      const averageDailyLoadValue =
                                        (dataPoint.totalLoad || 0) / 7;
                                      const maxYValue =
                                        Math.max(
                                          ...quarterlyData.map(
                                            (d) => (d.totalLoad || 0) / 7,
                                          ),
                                        ) * 1.1 || 100;
                                      const barValue =
                                        maxAverageDailyLoad > 0
                                          ? (averageDailyLoadValue /
                                              maxAverageDailyLoad) *
                                            maxYValue
                                          : 0;
                                      const barHeight =
                                        yScale(0) - yScale(barValue);

                                      return (
                                        <g key={`bar-group-${index}`}>
                                          {/* Bar */}
                                          <rect
                                            key={`bar-${index}`}
                                            x={barX}
                                            y={yScale(barValue)}
                                            width={barWidth}
                                            height={barHeight}
                                            fill={
                                              dataPoint.hasData
                                                ? "#9ca3af"
                                                : "#e5e7eb"
                                            }
                                            opacity={0.6}
                                          />
                                          {/* Weekly average value label on top of bar */}
                                          {averageDailyLoadValue > 0 && (
                                            <text
                                              key={`label-${index}`}
                                              x={xPos + offset}
                                              y={yScale(barValue) - 5}
                                              textAnchor="middle"
                                              fontSize="11"
                                              fill={
                                                isDark ? "#ffffff" : "#374151"
                                              }
                                              fontWeight="500"
                                            >
                                              {Math.round(
                                                averageDailyLoadValue,
                                              )}
                                            </text>
                                          )}
                                        </g>
                                      );
                                    })}
                                  </g>
                                );
                              },
                              // Custom layer for average reference lines
                              ({
                                xScale,
                                yScale,
                                margin,
                                width,
                                height,
                              }: any) => {
                                // Calculate the 12-week daily average
                                const quarterlyDailyAverage =
                                  quarterlyData.length > 0
                                    ? quarterlyData.reduce(
                                        (sum, d) => sum + d.totalLoad,
                                        0,
                                      ) /
                                      (quarterlyData.length * 7)
                                    : 0;

                                // Calculate the x-position of the first and last data points
                                const firstX =
                                  quarterlyData.length > 0
                                    ? xScale(quarterlyData[0].dateLabel)
                                    : 0;
                                const lastX =
                                  quarterlyData.length > 0
                                    ? xScale(
                                        quarterlyData[quarterlyData.length - 1]
                                          .dateLabel,
                                      )
                                    : width;

                                return (
                                  <g key="quarter-average-lines">
                                    {/* 12-Week Average Line (Green) */}
                                    {show12WeekAverage &&
                                      quarterlyDailyAverage > 0 && (
                                        <line
                                          x1={firstX}
                                          y1={yScale(quarterlyDailyAverage)}
                                          x2={lastX}
                                          y2={yScale(quarterlyDailyAverage)}
                                          stroke="#10b981"
                                          strokeWidth={2}
                                          strokeDasharray="8,4"
                                        />
                                      )}
                                    {/* 28-Day Average Line (Orange) */}
                                    {show28DayAverage &&
                                      last7DaysAverageLines.twentyEightDay >
                                        0 && (
                                        <line
                                          x1={firstX}
                                          y1={yScale(
                                            last7DaysAverageLines.twentyEightDay,
                                          )}
                                          x2={lastX}
                                          y2={yScale(
                                            last7DaysAverageLines.twentyEightDay,
                                          )}
                                          stroke="#f97316"
                                          strokeWidth={2}
                                          strokeDasharray="8,4"
                                        />
                                      )}
                                    {/* 7-Day Average Line (Purple) */}
                                    {show7DayAverage &&
                                      last7DaysAverageLines.sevenDay > 0 && (
                                        <line
                                          x1={firstX}
                                          y1={yScale(
                                            last7DaysAverageLines.sevenDay,
                                          )}
                                          x2={lastX}
                                          y2={yScale(
                                            last7DaysAverageLines.sevenDay,
                                          )}
                                          stroke="#7c3aed"
                                          strokeWidth={2}
                                          strokeDasharray="8,4"
                                        />
                                      )}
                                  </g>
                                );
                              },
                              // Custom layer for workload factor line (secondary y-axis)
                              ({ xScale, innerHeight, innerWidth }: any) => {
                                // Create a custom y-scale for workload factor (30% to 150%) - inverted so lower wf (overloading) plots higher
                                const wfYScale = (value: number) => {
                                  const min = 0.3;
                                  const max = 1.5;
                                  const normalized =
                                    (value - min) / (max - min);
                                  return normalized * innerHeight;
                                };

                                // Calculate the same offset used for bars - responsive width
                                const barWidth = innerWidth < 600 ? 20 : 40;
                                const allXPositions = quarterlyData.map((d) =>
                                  xScale(d.dateLabel),
                                );
                                const firstBarX =
                                  allXPositions[0] - barWidth / 2;
                                const offset =
                                  firstBarX < 0 ? Math.abs(firstBarX) + 2 : 0;

                                // Filter data points that have workload factor
                                const wfDataPoints = quarterlyData
                                  .map((d) => ({
                                    x: xScale(d.dateLabel) + offset,
                                    y: d.workloadFactor
                                      ? wfYScale(d.workloadFactor)
                                      : null,
                                    value: d.workloadFactor,
                                    label: d.dateLabel,
                                  }))
                                  .filter(
                                    (
                                      d,
                                    ): d is {
                                      x: number;
                                      y: number;
                                      value: number;
                                      label: string;
                                    } => d.y !== null && d.value !== undefined,
                                  );

                                if (wfDataPoints.length === 0) return null;

                                // Create path for the line
                                const pathData = wfDataPoints
                                  .map((d, i) =>
                                    i === 0
                                      ? `M ${d.x},${d.y}`
                                      : `L ${d.x},${d.y}`,
                                  )
                                  .join(" ");

                                return (
                                  <g key="workload-factor-line">
                                    {/* Load Balance Line */}
                                    <path
                                      d={pathData}
                                      fill="none"
                                      stroke={isDark ? "#ffffff" : "#000000"}
                                      strokeWidth={2}
                                    />
                                    {/* Points on the line */}
                                    {wfDataPoints.map((d, i) => (
                                      <circle
                                        key={`wf-point-${i}`}
                                        cx={d.x}
                                        cy={d.y}
                                        r={4}
                                        fill={isDark ? "#ffffff" : "#000000"}
                                        stroke="white"
                                        strokeWidth={2}
                                      />
                                    ))}
                                  </g>
                                );
                              },
                              "slices",
                            ]}
                            animate={true}
                          />
                        </div>

                        {/* Fixed metric values display */}
                        <div className="p-4">
                          {(() => {
                            // Use pinned slice if available, otherwise use hovered slice
                            const displaySlice = hoveredSlice || pinnedSlice;

                            if (!displaySlice || !displaySlice.points) {
                              return (
                                <div className="text-sm text-base-content/60 text-center">
                                  Hover over the graph to see values
                                </div>
                              );
                            }

                            // Get the date label from the slice
                            const dateLabel = displaySlice.points[0]?.data?.x;

                            // Find the corresponding quarterly data
                            const dataPoint = quarterlyData.find(
                              (d) => d.dateLabel === dateLabel,
                            );

                            if (!dataPoint) return null;

                            return (
                              <>
                                <div className="flex items-center justify-between mb-3">
                                  <div className="font-medium text-base-content">
                                    {dateLabel}
                                  </div>
                                  {pinnedSlice && !hoveredSlice && (
                                    <span className="text-xs text-base-content/60 italic">
                                      (pinned)
                                    </span>
                                  )}
                                </div>
                                <div className="space-y-2">
                                  {/* Weekly Average Load */}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <div
                                        className="w-4 h-4 rounded-full"
                                        style={{
                                          backgroundColor:
                                            "oklch(var(--bc) / 0.5)",
                                        }}
                                      />
                                      <span className="text-sm font-medium text-base-content/80">
                                        Weekly Avg Load
                                      </span>
                                    </div>
                                    <span
                                      className="text-lg font-bold"
                                      style={{
                                        color: "oklch(var(--bc) / 0.5)",
                                      }}
                                    >
                                      {Math.round(
                                        (dataPoint.totalLoad / 7) * 10,
                                      ) / 10}
                                    </span>
                                  </div>
                                  {/* Load Balance */}
                                  {dataPoint.workloadFactor !== undefined && (
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <div
                                          className="w-4 h-4 rounded-full"
                                          style={{
                                            backgroundColor: "oklch(var(--bc))",
                                          }}
                                        />
                                        <span className="text-sm font-medium text-base-content/80">
                                          Load Balance
                                        </span>
                                      </div>
                                      {(() => {
                                        const wf = dataPoint.workloadFactor;
                                        const diff = Math.round(100 - wf * 100);
                                        const title =
                                          wf < 0.75
                                            ? "Sharp Load Spike / High Stress"
                                            : wf < 0.9
                                              ? "Elevated Load / High Challenge"
                                              : wf < 1.0
                                                ? "Optimal Load / Building Phase"
                                                : wf < 1.1
                                                  ? "Optimal Load / Maintenance or Sharpening"
                                                  : wf < 1.25
                                                    ? "Reduced Load / Recovery or Taper"
                                                    : "Underloading / Deconditioning Risk";
                                        return (
                                          <div className="flex flex-col items-end">
                                            <span
                                              className="text-lg font-bold"
                                              style={{
                                                color: "oklch(var(--bc))",
                                              }}
                                            >{`${diff > 0 ? "+" : ""}${diff}%`}</span>
                                            <span className="text-xs text-base-content/60">
                                              {title}
                                            </span>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              </>
                            );
                          })()}
                        </div>

                        {/* Average indicators below chart */}
                        <div className="">
                          {quarterlyData.length > 0 && (
                            <div
                              onClick={() =>
                                setShow12WeekAverage(!show12WeekAverage)
                              }
                              className="flex items-center gap-3 mt-2 text-sm p-2 px-4 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={show12WeekAverage}
                                onChange={() =>
                                  setShow12WeekAverage(!show12WeekAverage)
                                }
                                className="checkbox"
                              />
                              <span className="font-heading text-lg font-bold text-emerald-600">
                                {Math.round(
                                  quarterlyData.reduce(
                                    (sum, d) => sum + d.totalLoad,
                                    0,
                                  ) /
                                    (quarterlyData.length * 7),
                                )}
                              </span>
                              <span className="font-medium text-xs text-base-content/60">
                                12 Week Daily Average
                              </span>
                            </div>
                          )}
                          {last7DaysAverageLines.twentyEightDay > 0 && (
                            <div
                              onClick={() =>
                                setShow28DayAverage(!show28DayAverage)
                              }
                              className="flex items-center gap-3 mt-2 text-sm p-2 px-4 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={show28DayAverage}
                                onChange={() =>
                                  setShow28DayAverage(!show28DayAverage)
                                }
                                className="checkbox"
                              />
                              <span className="font-heading text-lg font-bold text-orange-600">
                                {Math.round(
                                  last7DaysAverageLines.twentyEightDay,
                                )}
                              </span>
                              <span className="font-medium text-xs text-base-content/60">
                                28 Day Daily Average
                              </span>
                            </div>
                          )}
                          {last7DaysAverageLines.sevenDay > 0 && (
                            <div
                              onClick={() =>
                                setShow7DayAverage(!show7DayAverage)
                              }
                              className="flex items-center gap-3 mt-2 text-sm p-2 px-4 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={show7DayAverage}
                                onChange={() =>
                                  setShow7DayAverage(!show7DayAverage)
                                }
                                className="checkbox"
                              />
                              <span className="font-heading text-lg font-bold text-purple-600">
                                {Math.round(last7DaysAverageLines.sevenDay)}
                              </span>
                              <span className="font-medium text-xs text-base-content/60">
                                7 Day Daily Average
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Load Breakdown for Quarter view */}
                        <div
                          className={`mt-8 border-t border-base-300 pt-10 ${
                            !hasQuarterlyAccess
                              ? "blur-md pointer-events-none"
                              : ""
                          }`}
                        >
                          <h3 className="text-xl font-heading font-semibold text-base-content">
                            Load Breakdown
                          </h3>
                          <p className="text-xs text-base-content/60 mb-4">
                            By Session Type (Last 12 Weeks)
                          </p>
                          <div className="w-full">
                            {quarterlySessionTypeBreakdown.length > 0 ? (
                              <>
                                <div className="space-y-3">
                                  {(() => {
                                    const totalLoad =
                                      quarterlySessionTypeBreakdown.reduce(
                                        (sum, st) => sum + st.totalLoad,
                                        0,
                                      );

                                    const itemsToShow = showAllQuarterBreakdown
                                      ? quarterlySessionTypeBreakdown
                                      : quarterlySessionTypeBreakdown.slice(
                                          0,
                                          5,
                                        );

                                    return itemsToShow.map((item, index) => {
                                      const percentage =
                                        totalLoad > 0
                                          ? (item.totalLoad / totalLoad) * 100
                                          : 0;

                                      return (
                                        <div key={index} className="relative">
                                          <div className="flex items-center justify-between mb-2">
                                            <span className="text-md font-medium text-base-content">
                                              {item.sessionType}
                                            </span>
                                            <span className="text-sm font-bold text-base-content">
                                              {percentage.toFixed(1)}%
                                            </span>
                                          </div>
                                          <div className="w-full bg-base-300 rounded-md h-7 overflow-hidden mb-4">
                                            <div
                                              className="bg-base-content/60 h-full rounded-md transition-all duration-500 flex items-center justify-end pr-3"
                                              style={{
                                                width: `${percentage}%`,
                                              }}
                                            >
                                              {percentage > 15 && (
                                                <span className="text-white text-xs font-bold">
                                                  {percentage.toFixed(1)}%
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    });
                                  })()}
                                </div>
                                {quarterlySessionTypeBreakdown.length > 5 && (
                                  <button
                                    onClick={() =>
                                      setShowAllQuarterBreakdown(
                                        !showAllQuarterBreakdown,
                                      )
                                    }
                                    className="mt-4 w-full px-4 py-2 text-sm font-medium text-base-content/80 bg-base-200 rounded-md hover:bg-base-300 transition-colors"
                                  >
                                    {showAllQuarterBreakdown
                                      ? "Show Less"
                                      : `Load More (${
                                          quarterlySessionTypeBreakdown.length -
                                          5
                                        } more)`}
                                  </button>
                                )}
                              </>
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <p className="text-base-content/60 text-sm">
                                  No load data available for the last 12 weeks
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* Custom Date Range View */}
                  {selectedTab === "Custom" && customRangeData.length > 0 && (
                    <div className="space-y-8 bg-base-100 rounded-lg shadow-sm p-8">
                      <div>
                        <h3 className="text-xl font-heading font-semibold text-base-content">
                          Daily Load
                        </h3>
                        <p className="text-xs text-base-content/60 mb-4">
                          {customStartDate && customEndDate && (
                            <>
                              {new Date(customStartDate).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              )}{" "}
                              -{" "}
                              {new Date(customEndDate).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              )}
                            </>
                          )}
                        </p>
                      </div>

                      {/* Bar Chart */}
                      <div className="h-80 w-full">
                        <ResponsiveBar
                          theme={nivoTheme}
                          data={customRangeData as any}
                          keys={["totalLoad"]}
                          indexBy="dateLabel"
                          margin={{
                            top: 20,
                            right: 20,
                            bottom: 80,
                            left: 50,
                          }}
                          padding={0.3}
                          valueScale={{ type: "linear" }}
                          indexScale={{ type: "band", round: true }}
                          colors="#9ca3af"
                          borderRadius={4}
                          axisTop={null}
                          axisRight={null}
                          axisBottom={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: -45,
                            legend: "",
                            legendPosition: "middle",
                            legendOffset: 32,
                          }}
                          axisLeft={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: "Load",
                            legendPosition: "middle",
                            legendOffset: -40,
                          }}
                          labelSkipWidth={12}
                          labelSkipHeight={12}
                          labelTextColor="#ffffff"
                          animate={true}
                          motionConfig="gentle"
                        />
                      </div>

                      {/* Load Breakdown for Custom view */}
                      <div className="mt-8 border-t border-base-300 pt-10">
                        <h3 className="text-xl font-heading font-semibold text-base-content">
                          Load Breakdown
                        </h3>
                        <p className="text-xs text-base-content/60 mb-4">
                          By Session Type (Custom Range)
                        </p>
                        <div className="w-full">
                          {customRangeSessionTypeBreakdown.length > 0 ? (
                            <>
                              <div className="space-y-3">
                                {(() => {
                                  const totalLoad =
                                    customRangeSessionTypeBreakdown.reduce(
                                      (sum, st) => sum + st.totalLoad,
                                      0,
                                    );

                                  const itemsToShow = showAllCustomBreakdown
                                    ? customRangeSessionTypeBreakdown
                                    : customRangeSessionTypeBreakdown.slice(
                                        0,
                                        5,
                                      );

                                  return itemsToShow.map((item, index) => {
                                    const percentage =
                                      totalLoad > 0
                                        ? (item.totalLoad / totalLoad) * 100
                                        : 0;

                                    return (
                                      <div key={index} className="relative">
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-md font-medium text-base-content">
                                            {item.sessionType}
                                          </span>
                                          <span className="text-sm font-bold text-base-content">
                                            {percentage.toFixed(1)}%
                                          </span>
                                        </div>
                                        <div className="w-full bg-base-300 rounded-md h-7 overflow-hidden mb-4">
                                          <div
                                            className="bg-base-content/60 h-full rounded-md transition-all duration-500 flex items-center justify-end pr-3"
                                            style={{ width: `${percentage}%` }}
                                          >
                                            {percentage > 15 && (
                                              <span className="text-white text-xs font-bold">
                                                {percentage.toFixed(1)}%
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                              {customRangeSessionTypeBreakdown.length > 5 && (
                                <button
                                  onClick={() =>
                                    setShowAllCustomBreakdown(
                                      !showAllCustomBreakdown,
                                    )
                                  }
                                  className="mt-4 w-full px-4 py-2 text-sm font-medium text-base-content/80 bg-base-200 rounded-md hover:bg-base-300 transition-colors"
                                >
                                  {showAllCustomBreakdown
                                    ? "Show Less"
                                    : `Load More (${
                                        customRangeSessionTypeBreakdown.length -
                                        5
                                      } more)`}
                                </button>
                              )}
                            </>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <p className="text-base-content/60 text-sm">
                                No load data available for the selected range
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Daily Training Load Bar Chart - Week view (same style as PeakInsights) */}
                  {selectedTab === "Week" && trainingData.length > 0 && (
                    <div className="space-y-8 p-8 pt-0">
                      <div>
                        <h3 className="text-xl font-heading font-semibold text-base-content">
                          Last 7 Days
                        </h3>
                        <p className="text-xs text-base-content/60 mb-4">
                          Daily Average Load
                        </p>
                        <div className="h-80 w-full">
                          <ResponsiveLine
                            theme={nivoTheme}
                            data={[
                              {
                                id: "trainingLoad",
                                data: trainingData.map((d) => ({
                                  x: d.dateLabel,
                                  y: d.totalLoad, // Use actual totalLoad for slice interaction
                                })),
                              },
                            ]}
                            margin={{
                              top: 40,
                              right: 60,
                              bottom: 100,
                              left: 50,
                            }}
                            xScale={{ type: "point" }}
                            yScale={{
                              type: "linear",
                              min: 0,
                              max:
                                Math.max(
                                  ...trainingData.map((d) => d.totalLoad || 0),
                                ) * 1.1 || 100,
                              stacked: false,
                            }}
                            axisTop={null}
                            axisRight={{
                              tickSize: 0,
                              tickPadding: 10,
                              tickRotation: 0,
                              legend: "",
                              legendPosition: "middle",
                              legendOffset: 45,
                              tickValues: [],
                              format: (value: any) => "",
                            }}
                            axisBottom={{
                              tickSize: 5,
                              tickPadding: 5,
                              tickRotation: -45,
                              legend: "",
                              legendPosition: "middle",
                              legendOffset: 50,
                              tickValues: trainingData.map((d) => d.dateLabel),
                            }}
                            axisLeft={{
                              tickSize: 0,
                              tickPadding: 10,
                              tickRotation: 0,
                              legend: "",
                              legendPosition: "middle",
                              legendOffset: -40,
                              tickValues: 5,
                              format: (value: any) =>
                                Math.round(value).toString(),
                            }}
                            enableGridX={false}
                            enableGridY={true}
                            enablePoints={false}
                            enableArea={false}
                            useMesh={true}
                            enableSlices="x"
                            onClick={(point: any) => {
                              // When clicking on the chart, pin the current slice
                              if (hoveredSlice) {
                                setPinnedSlice(hoveredSlice);
                              }
                            }}
                            onMouseLeave={() => {
                              setHoveredSlice(null);
                            }}
                            sliceTooltip={({ slice }: any) => {
                              // Use setTimeout to update state after render
                              console.log("Week view - slice:", slice);
                              setTimeout(() => setHoveredSlice(slice), 0);
                              return null;
                            }}
                            layers={[
                              "grid",
                              "markers",
                              "axes",
                              // Custom layer for training load bars (same style as PeakInsights)
                              ({ xScale, yScale, width, height }: any) => {
                                const maxTrainingLoad = Math.max(
                                  ...trainingData.map((d) => d.totalLoad || 0),
                                );

                                if (maxTrainingLoad === 0) return null;

                                const barWidth = 40; // Wider bars for better visibility

                                // Get all x positions to calculate offset
                                const allXPositions = trainingData.map((d) =>
                                  xScale(d.dateLabel),
                                );

                                // Calculate offset to ensure bars don't get clipped
                                // If the first bar would be clipped, shift all bars right
                                const firstBarX =
                                  allXPositions[0] - barWidth / 2;
                                const offset =
                                  firstBarX < 0 ? Math.abs(firstBarX) + 2 : 0; // +2 for small buffer

                                return (
                                  <g key="training-load-bars">
                                    {trainingData.map((dataPoint, index) => {
                                      const xPos = xScale(dataPoint.dateLabel);
                                      const barX = xPos - barWidth / 2 + offset;

                                      // Scale bar height relative to max training load
                                      const trainingLoadValue =
                                        dataPoint.totalLoad || 0;
                                      const maxYValue =
                                        Math.max(
                                          ...trainingData.map(
                                            (d) => d.totalLoad || 0,
                                          ),
                                        ) * 1.1 || 100;
                                      const barValue =
                                        maxTrainingLoad > 0
                                          ? (trainingLoadValue /
                                              maxTrainingLoad) *
                                            maxYValue
                                          : 0;
                                      const barHeight =
                                        yScale(0) - yScale(barValue);

                                      return (
                                        <g key={`bar-group-${index}`}>
                                          {/* Bar */}
                                          <rect
                                            key={`bar-${index}`}
                                            x={barX}
                                            y={yScale(barValue)}
                                            width={barWidth}
                                            height={barHeight}
                                            fill={
                                              dataPoint.hasData
                                                ? "#9ca3af"
                                                : "#e5e7eb"
                                            } // Same colors as PeakInsights
                                            opacity={0.6}
                                          />
                                          {/* Training load value label on top of bar */}
                                          {trainingLoadValue > 0 && (
                                            <text
                                              key={`label-${index}`}
                                              x={xPos + offset}
                                              y={yScale(barValue) - 5} // 5px above the bar
                                              textAnchor="middle"
                                              fontSize="11"
                                              fill={
                                                isDark ? "#ffffff" : "#374151"
                                              }
                                              fontWeight="500"
                                            >
                                              {Math.round(trainingLoadValue)}
                                            </text>
                                          )}
                                        </g>
                                      );
                                    })}
                                  </g>
                                );
                              },
                              // Custom layer for average lines
                              ({
                                xScale,
                                yScale,
                                margin,
                                width,
                                height,
                              }: any) => {
                                // Calculate the x-position of the first and last data points
                                const firstX =
                                  trainingData.length > 0
                                    ? xScale(trainingData[0].dateLabel)
                                    : 0;
                                const lastX =
                                  trainingData.length > 0
                                    ? xScale(
                                        trainingData[trainingData.length - 1]
                                          .dateLabel,
                                      )
                                    : width;

                                return (
                                  <g key="average-lines">
                                    {/* 7-Day Average Line (Purple) */}
                                    {show7DayAverage &&
                                      last7DaysAverageLines.sevenDay > 0 && (
                                        <g>
                                          <line
                                            x1={firstX}
                                            y1={yScale(
                                              last7DaysAverageLines.sevenDay,
                                            )}
                                            x2={lastX}
                                            y2={yScale(
                                              last7DaysAverageLines.sevenDay,
                                            )}
                                            stroke="#7c3aed"
                                            strokeWidth={2}
                                            strokeDasharray="8,4"
                                          />
                                        </g>
                                      )}
                                    {/* 28-Day Average Line (Orange) */}
                                    {show28DayAverage &&
                                      last7DaysAverageLines.twentyEightDay >
                                        0 && (
                                        <g>
                                          <line
                                            x1={firstX}
                                            y1={yScale(
                                              last7DaysAverageLines.twentyEightDay,
                                            )}
                                            x2={lastX}
                                            y2={yScale(
                                              last7DaysAverageLines.twentyEightDay,
                                            )}
                                            stroke="#f97316"
                                            strokeWidth={2}
                                            strokeDasharray="8,4"
                                          />
                                        </g>
                                      )}
                                    {/* Previous 7-Day Average Line (Gray) */}
                                    {showPrevious7DayAverage &&
                                      last7DaysAverageLines.previous7Day >
                                        0 && (
                                        <g>
                                          <line
                                            x1={firstX}
                                            y1={yScale(
                                              last7DaysAverageLines.previous7Day,
                                            )}
                                            x2={lastX}
                                            y2={yScale(
                                              last7DaysAverageLines.previous7Day,
                                            )}
                                            stroke="#6b7280"
                                            strokeWidth={2}
                                            strokeDasharray="8,4"
                                          />
                                        </g>
                                      )}
                                  </g>
                                );
                              },
                              // Custom layer for workload factor line (secondary y-axis)
                              ({ xScale, innerHeight }: any) => {
                                // Create a custom y-scale for workload factor (30% to 150%) - inverted so lower wf (overloading) plots higher
                                const wfYScale = (value: number) => {
                                  const min = 0.3;
                                  const max = 1.5;
                                  const normalized =
                                    (value - min) / (max - min);
                                  return normalized * innerHeight;
                                };

                                // Calculate the same offset used for bars
                                const barWidth = 40;
                                const allXPositions = trainingData.map((d) =>
                                  xScale(d.dateLabel),
                                );
                                const firstBarX =
                                  allXPositions[0] - barWidth / 2;
                                const offset =
                                  firstBarX < 0 ? Math.abs(firstBarX) + 2 : 0;

                                // Filter data points that have workload factor
                                const wfDataPoints = trainingData
                                  .map((d) => ({
                                    x: xScale(d.dateLabel) + offset,
                                    y: d.workloadFactor
                                      ? wfYScale(d.workloadFactor)
                                      : null,
                                    value: d.workloadFactor,
                                    label: d.dateLabel,
                                  }))
                                  .filter(
                                    (
                                      d,
                                    ): d is {
                                      x: number;
                                      y: number;
                                      value: number;
                                      label: string;
                                    } => d.y !== null && d.value !== undefined,
                                  );

                                if (wfDataPoints.length === 0) return null;

                                // Create path for the line
                                const pathData = wfDataPoints
                                  .map((d, i) =>
                                    i === 0
                                      ? `M ${d.x},${d.y}`
                                      : `L ${d.x},${d.y}`,
                                  )
                                  .join(" ");

                                return (
                                  <g key="workload-factor-line">
                                    {/* Load Balance Line */}
                                    <path
                                      d={pathData}
                                      fill="none"
                                      stroke={isDark ? "#ffffff" : "#000000"}
                                      strokeWidth={2}
                                    />
                                    {/* Points on the line */}
                                    {wfDataPoints.map((d, i) => (
                                      <circle
                                        key={`wf-point-${i}`}
                                        cx={d.x}
                                        cy={d.y}
                                        r={4}
                                        fill={isDark ? "#ffffff" : "#000000"}
                                        stroke="white"
                                        strokeWidth={2}
                                      />
                                    ))}
                                  </g>
                                );
                              },
                              "slices",
                            ]}
                            animate={true}
                          />
                        </div>

                        {/* Fixed metric values display */}
                        <div className="px-4">
                          {(() => {
                            // Use pinned slice if available, otherwise use hovered slice
                            const displaySlice = hoveredSlice || pinnedSlice;

                            console.log(
                              "Week view - displaySlice:",
                              displaySlice,
                            );
                            console.log(
                              "Week view - hoveredSlice:",
                              hoveredSlice,
                            );
                            console.log(
                              "Week view - pinnedSlice:",
                              pinnedSlice,
                            );

                            if (!displaySlice || !displaySlice.points) {
                              return (
                                <div className="text-sm text-base-content/60 text-center">
                                  Hover over the graph to see values
                                </div>
                              );
                            }

                            // Get the date label from the slice
                            const dateLabel = displaySlice.points[0]?.data?.x;
                            console.log("Week view - dateLabel:", dateLabel);

                            // Find the corresponding training data
                            const dataPoint = trainingData.find(
                              (d) => d.dateLabel === dateLabel,
                            );

                            if (!dataPoint) return null;

                            return (
                              <>
                                <div className="flex items-center justify-between mb-3">
                                  <div className="font-medium text-base-content">
                                    {dateLabel}
                                  </div>
                                  {pinnedSlice && !hoveredSlice && (
                                    <span className="text-xs text-base-content/60 italic">
                                      (pinned)
                                    </span>
                                  )}
                                </div>
                                <div className="space-y-2">
                                  {/* Training Load */}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <div
                                        className="w-4 h-4 rounded-full"
                                        style={{
                                          backgroundColor:
                                            "oklch(var(--bc) / 0.5)",
                                        }}
                                      />
                                      <span className="text-sm font-medium text-base-content/80">
                                        Training Load
                                      </span>
                                    </div>
                                    <span
                                      className="text-lg font-bold"
                                      style={{
                                        color: "oklch(var(--bc) / 0.5)",
                                      }}
                                    >
                                      {Math.round(dataPoint.totalLoad)}
                                    </span>
                                  </div>
                                  {/* Load Balance */}
                                  {dataPoint.workloadFactor !== undefined && (
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <div
                                          className="w-4 h-4 rounded-full"
                                          style={{
                                            backgroundColor: "oklch(var(--bc))",
                                          }}
                                        />
                                        <span className="text-sm font-medium text-base-content/80">
                                          Load Balance
                                        </span>
                                      </div>
                                      {(() => {
                                        const wf = dataPoint.workloadFactor;
                                        const diff = Math.round(100 - wf * 100);
                                        const title =
                                          wf < 0.75
                                            ? "Sharp Load Spike / High Stress"
                                            : wf < 0.9
                                              ? "Elevated Load / High Challenge"
                                              : wf < 1.0
                                                ? "Optimal Load / Building Phase"
                                                : wf < 1.1
                                                  ? "Optimal Load / Maintenance or Sharpening"
                                                  : wf < 1.25
                                                    ? "Reduced Load / Recovery or Taper"
                                                    : "Underloading / Deconditioning Risk";
                                        return (
                                          <div className="flex flex-col items-end">
                                            <span
                                              className="text-lg font-bold"
                                              style={{
                                                color: "oklch(var(--bc))",
                                              }}
                                            >{`${diff > 0 ? "+" : ""}${diff}%`}</span>
                                            <span className="text-xs text-base-content/60">
                                              {title}
                                            </span>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              </>
                            );
                          })()}
                        </div>

                        {/*CORRECT STYLE Average indicators below chart */}
                        <div className="">
                          {last7DaysAverageLines.twentyEightDay > 0 && (
                            <div
                              onClick={() =>
                                setShow28DayAverage(!show28DayAverage)
                              }
                              className="flex items-center gap-3 mt-2 text-sm p-2 px-4 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={show28DayAverage}
                                onChange={() =>
                                  setShow28DayAverage(!show28DayAverage)
                                }
                                className="checkbox"
                              />
                              <span className="font-heading text-lg font-bold text-orange-600">
                                {Math.round(
                                  last7DaysAverageLines.twentyEightDay,
                                )}
                              </span>
                              <span className="font-medium text-xs text-base-content/60">
                                28 Day Daily Average
                              </span>
                            </div>
                          )}
                          {last7DaysAverageLines.sevenDay > 0 && (
                            <div
                              onClick={() =>
                                setShow7DayAverage(!show7DayAverage)
                              }
                              className="flex items-center gap-3 mt-2 text-sm p-2 px-4 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={show7DayAverage}
                                onChange={() =>
                                  setShow7DayAverage(!show7DayAverage)
                                }
                                className="checkbox"
                              />
                              <span className="font-heading text-lg font-bold text-purple-600">
                                {Math.round(last7DaysAverageLines.sevenDay)}
                              </span>
                              <span className="font-medium text-xs text-base-content/60">
                                7 Day Daily Average
                              </span>
                            </div>
                          )}

                          {/* Previous 7 Days Average - comparison value */}
                          {last7DaysAverageLines.previous7Day > 0 && (
                            <div className="mt-2 text-sm p-2 px-4">
                              <div
                                onClick={() =>
                                  setShowPrevious7DayAverage(
                                    !showPrevious7DayAverage,
                                  )
                                }
                                className="flex items-center gap-3 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={showPrevious7DayAverage}
                                  onChange={() =>
                                    setShowPrevious7DayAverage(
                                      !showPrevious7DayAverage,
                                    )
                                  }
                                  className="checkbox"
                                />
                                <span className="font-heading text-lg font-bold text-base-content/80">
                                  {Math.round(
                                    last7DaysAverageLines.previous7Day,
                                  )}
                                </span>
                                <span className="font-medium text-xs text-base-content/60">
                                  Previous 7 Days Daily Average
                                </span>
                              </div>
                              {/* Percentage difference between last 7 days and previous 7 days */}
                              {last7DaysAverageLines.sevenDay > 0 && (
                                <div className="ml-7 mt-1">
                                  {(() => {
                                    const percentDiff =
                                      ((last7DaysAverageLines.sevenDay -
                                        last7DaysAverageLines.previous7Day) /
                                        last7DaysAverageLines.previous7Day) *
                                      100;
                                    const isPositive = percentDiff > 0;
                                    const isNegative = percentDiff < 0;

                                    return (
                                      <div className="flex items-center gap-2">
                                        <span
                                          className={`font-heading text-xs font-bold ${
                                            isPositive
                                              ? "text-green-600"
                                              : isNegative
                                                ? "text-red-600"
                                                : "text-base-content/70"
                                          }`}
                                        >
                                          {isPositive ? "+" : ""}
                                          {percentDiff.toFixed(1)}%
                                        </span>
                                        <span className="text-base-content/50 font-medium text-xs">
                                          Last 7 days vs previous 7 days
                                        </span>
                                      </div>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Load Breakdown for Week view */}
                        <div className="mt-8 border-t border-base-300 pt-10">
                          <h3 className="text-xl font-heading font-semibold text-base-content">
                            Load Breakdown
                          </h3>
                          <p className="text-xs text-base-content/60 mb-4">
                            By Session Type (Last 7 Days)
                          </p>
                          <div className="w-full relative">
                            {
                              // Week view - Session type breakdown with horizontal bars
                              sessionTypeBreakdown.length > 0 ? (
                                <>
                                  <div className="space-y-3">
                                    {(() => {
                                      const totalLoad =
                                        sessionTypeBreakdown.reduce(
                                          (sum, st) => sum + st.totalLoad,
                                          0,
                                        );

                                      const itemsToShow = showAllWeekBreakdown
                                        ? sessionTypeBreakdown
                                        : sessionTypeBreakdown.slice(0, 5);

                                      return itemsToShow.map((item, index) => {
                                        const percentage =
                                          totalLoad > 0
                                            ? (item.totalLoad / totalLoad) * 100
                                            : 0;

                                        return (
                                          <div key={index} className="relative">
                                            <div className="flex items-center justify-between mb-2">
                                              <span className="text-md font-medium text-base-content">
                                                {item.sessionType}
                                              </span>
                                              <span className="text-sm font-bold text-base-content">
                                                {percentage.toFixed(1)}%
                                              </span>
                                            </div>
                                            <div className="w-full bg-base-300 rounded-md h-7 overflow-hidden mb-4">
                                              <div
                                                className="bg-base-content/60 h-full rounded-md transition-all duration-500 flex items-center justify-end pr-3"
                                                style={{
                                                  width: `${percentage}%`,
                                                }}
                                              >
                                                {percentage > 15 && (
                                                  <span className="text-white text-xs font-bold">
                                                    {percentage.toFixed(1)}%
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      });
                                    })()}
                                  </div>
                                  {sessionTypeBreakdown.length > 5 && (
                                    <button
                                      onClick={() =>
                                        setShowAllWeekBreakdown(
                                          !showAllWeekBreakdown,
                                        )
                                      }
                                      className="mt-4 w-full px-4 py-2 text-sm font-medium text-base-content/80 bg-base-200 rounded-md hover:bg-base-300 transition-colors"
                                    >
                                      {showAllWeekBreakdown
                                        ? "Show Less"
                                        : `Load More (${
                                            sessionTypeBreakdown.length - 5
                                          } more)`}
                                    </button>
                                  )}
                                </>
                              ) : (
                                <div className="flex items-center justify-center h-full">
                                  <p className="text-base-content/60 text-sm">
                                    No load data available for the last 7 days
                                  </p>
                                </div>
                              )
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : loading ? (
                <div className="text-center py-12">
                  <div className="animate-pulse">
                    <div className="h-8 bg-base-300 rounded w-1/3 mx-auto mb-4"></div>
                    <div className="h-80 bg-base-200 rounded"></div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-base-content/70">
                    No training data available for this period
                  </p>
                </div>
              )}
            </div>

            {/* Custom Date Range Modal */}
            {showCustomDateModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-base-100 rounded-lg max-w-md w-full">
                  <div className="p-8">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-grotesk font-semibold text-base-content">
                        Custom Date Range
                      </h3>
                      <button
                        onClick={() => {
                          setShowCustomDateModal(false);
                          if (customRangeData.length === 0) {
                            setSelectedTab("Day");
                          }
                        }}
                        className="p-1 rounded-full hover:bg-base-200 transition-colors"
                      >
                        <XMarkIcon className="h-5 w-5 text-base-content/60" />
                      </button>
                    </div>

                    {!hasCustomDateAccess && (
                      <ComingSoon featureKey="advancedGraphs" />
                    )}

                    <div className="space-y-4">
                      {/* Date pickers - always visible but disabled if no access */}
                      <div className="new-form-group w-full">
                        <input
                          id="custom-start-date"
                          type="date"
                          className={`form-element ${
                            hasCustomDateAccess
                              ? "cursor-pointer"
                              : "cursor-not-allowed opacity-60"
                          }`}
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          max={customEndDate || undefined}
                          disabled={!hasCustomDateAccess}
                          style={{
                            colorScheme: "light",
                            backgroundColor: "oklch(var(--b2))",
                            // iOS fix: Prevent date input from extending beyond container
                            maxWidth: "100%",
                            width: "100%",
                            fontSize: "16px", // Prevents iOS zoom on focus
                          }}
                          onFocus={(e) => {
                            if (hasCustomDateAccess) {
                              try {
                                e.target.showPicker?.();
                              } catch (error) {
                                // Fallback for browsers that don't support showPicker
                              }
                            }
                          }}
                        />
                        <label className="text-base-content/80">
                          Start Date
                        </label>
                      </div>

                      <div className="new-form-group w-full">
                        <input
                          id="custom-end-date"
                          type="date"
                          className={`form-element ${
                            hasCustomDateAccess
                              ? "cursor-pointer"
                              : "cursor-not-allowed opacity-60"
                          }`}
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          min={customStartDate || undefined}
                          disabled={!hasCustomDateAccess}
                          style={{
                            colorScheme: "light",
                            backgroundColor: "oklch(var(--b2))",
                            // iOS fix: Prevent date input from extending beyond container
                            maxWidth: "100%",
                            width: "100%",
                            fontSize: "16px", // Prevents iOS zoom on focus
                          }}
                          onFocus={(e) => {
                            if (hasCustomDateAccess) {
                              try {
                                e.target.showPicker?.();
                              } catch (error) {
                                // Fallback for browsers that don't support showPicker
                              }
                            }
                          }}
                        />
                        <label className="text-base-content/80">End Date</label>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={async () => {
                            await fetchCustomRangeData(
                              customStartDate,
                              customEndDate,
                            );
                            setSelectedTab("Custom");
                            setShowCustomDateModal(false);
                          }}
                          disabled={
                            !hasCustomDateAccess ||
                            !customStartDate ||
                            !customEndDate ||
                            customRangeLoading
                          }
                          className="flex-1 px-4 py-3 bg-primary text-white font-grotesk rounded-xl hover:bg-primary/90 transition-colors disabled:bg-base-300 disabled:text-base-content/60 disabled:cursor-not-allowed"
                        >
                          {customRangeLoading ? "Loading..." : "Load Data"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Export Dialog */}
            {showExportDialog && (
              <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-base-100 rounded-lg max-w-md w-full">
                  <div className="p-8">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-grotesk font-bold text-base-content">
                        Export Data to CSV
                      </h3>

                      <button
                        onClick={() => setShowExportDialog(false)}
                        className="p-1 rounded-full hover:bg-base-200 transition-colors"
                      >
                        <XMarkIcon className="h-5 w-5 text-base-content/60" />
                      </button>
                    </div>
                    {!isPremium && <ComingSoon featureKey="advancedGraphs" />}
                    <div className="mt-6 flex justify-end">
                      <button
                        onClick={() => setShowExportDialog(false)}
                        className="px-4 py-2 bg-base-200 text-base-content/80 rounded-md hover:bg-base-300 transition-colors font-medium font-grotesk"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Info Dialog */}
            {showInfoDialog && (
              <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-base-100 rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto">
                  <div className="p-8">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-grotesk font-bold text-base-content">
                        Training Load
                      </h3>
                      <button
                        onClick={() => setShowInfoDialog(false)}
                        className="p-1 rounded-full hover:bg-base-200 transition-colors"
                      >
                        <XMarkIcon className="h-5 w-5 text-base-content/60" />
                      </button>
                    </div>

                    <div className="space-y-4 text-sm text-base-content/80">
                      <p className="text-lg font-grotesk">
                        Training load helps you understand how much stress your
                        body is under across days and weeks, not just in a
                        single session, which is why logging every session
                        matters, from hard workouts to walks or mobility work.
                        This complete picture feeds into your Peak Score,
                        helping it reflect how prepared you are to handle
                        training based on what you’ve been doing and how your
                        body has been responding.
                      </p>

                      <div>
                        <h4 className="font-bold font-grotesk text-lg text-base-content mb-2">
                          What it measures:
                        </h4>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>Session RPE (Rate of Perceived Exertion)</li>
                          <li>Training duration and intensity</li>
                          <li>Total weekly and monthly load</li>
                          <li>Load distribution across training types</li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="font-bold font-grotesk text-lg text-base-content mb-2">
                          Load calculation:
                        </h4>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>
                            <strong>Session Load:</strong> Duration (time) × RPE
                            (Respiratory and Muscular Effort) x Load Modifier
                          </li>
                          <li>
                            <strong>Load Modifier:</strong> refines how
                            different training sessions reflect the specific
                            demands of a session.
                          </li>
                          <li>
                            <strong>Load Balance:</strong> measures how your
                            recent (acute) training load compares to your
                            medium- to long-term (chronic) load
                          </li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="font-bold font-grotesk text-lg text-base-content mb-2">
                          Load Balance
                        </h4>
                        <p>
                          Load Balance compares how much training you've done
                          recently (acute load) to what your body has been
                          prepared for over time (chronic load). It shows
                          whether you are overloading, adapting, maintaining, or
                          unloading relative to your established capacity.
                        </p>
                        <br></br>
                        <p className="text-base-content/70">
                          The percentage shown on your Load Balance widget
                          tells you how far your recent training is above or
                          below your established capacity — a positive
                          percentage means you're pushing harder than usual, a
                          negative percentage means you're loading less than
                          usual.
                        </p>
                        <br></br>
                        <p>
                          <strong className="text-lg">Above +25%</strong>
                          <br></br>
                          <strong>Sharp Load Spike / High Stress</strong>
                          <br></br>
                          Recent training is much higher than what your body has
                          been prepared for. Fatigue accumulates quickly and
                          injury risk is elevated if this is sustained.
                          <br></br>
                          <br></br>
                          <strong className="text-lg">+10% to +25%</strong>
                          <br></br>
                          <strong>Elevated Load / High Challenge</strong>
                          <br></br>
                          Training demand is above your recent capacity. This
                          zone can drive adaptation, but recovery quality
                          becomes critical.
                          <br></br>
                          <br></br>
                          <strong className="text-lg">0% to +10%</strong>
                          <br></br>
                          <strong>Optimal Load / Building Phase</strong>
                          <br></br>
                          Recent training is slightly higher than your
                          established capacity. This is an ideal zone for
                          building fitness and progressive overload.
                          <br></br>
                          <br></br>
                          <strong className="text-lg">-10% to 0%</strong>
                          <br></br>
                          <strong>
                            Optimal Load / Maintenance or Sharpening
                          </strong>
                          <br></br>
                          Recent training closely matches your established
                          capacity. This supports consistency, performance
                          stability, and readiness.
                          <br></br>
                          <br></br>
                          <strong className="text-lg">-25% to -10%</strong>
                          <br></br>
                          <strong>Reduced Load / Recovery or Taper</strong>
                          <br></br>
                          Training demand is lower than what you are conditioned
                          for. Useful for deloads, recovery blocks, or
                          pre-competition tapering.
                          <br></br>
                          <br></br>
                          <strong className="text-lg">Below -25%</strong>
                          <br></br>
                          <strong>Underloading / Deconditioning Risk</strong>
                          <br></br>
                          Training load is well below your capacity. If
                          prolonged, fitness may decline and performance
                          readiness can drop.
                        </p>
                      </div>

                      <p className="text-base-content/70">
                        Monitor your workload factor to prevent overtraining,
                        optimise performance gains, and ensure adequate recovery
                        between sessions.
                      </p>
                    </div>

                    <div className="mt-6 flex justify-end">
                      <button
                        onClick={() => setShowInfoDialog(false)}
                        className="px-4 py-2 bg-neutral text-white rounded-md hover:bg-neutral/90 transition-colors font-medium font-grotesk"
                      >
                        Okay, got it
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Training Log Form Modal */}
          <TrainingLogForm
            open={showTrainingForm}
            onClose={handleCloseTrainingForm}
            context={{
              dateID:
                editingTrainingLog?.data?.dateID ||
                generateDateID(targetDate),
            }}
            editMode={!!editingTrainingLog}
            logId={editingTrainingLog?.id}
            initialData={editingTrainingLog?.data}
            onSaved={async () => {
              setShowTrainingForm(false);
              setEditingTrainingLog(null);
              await fetchDayPostsData(false);
            }}
          />

          <DeleteTrainingLogDialog
            open={!!trainingLogToDelete}
            log={trainingLogToDelete}
            isDeleting={isDeletingTrainingLog}
            onCancel={() => setTrainingLogToDelete(null)}
            onConfirm={handleConfirmDeleteTrainingLog}
          />
        </>
      )}
    </div>
  );
};

export default TrainingInsights;
